import { PipecatClient, RTVIEvent } from '@pipecat-ai/client-js';
import { DailyTransport } from '@pipecat-ai/daily-transport';

// Initialize global context variable for Audio Call
if (!window.audioCallContextDocuments) {
    window.audioCallContextDocuments = [];
}
if (typeof window.lexiusCallMicrophoneActive !== 'boolean') {
    window.lexiusCallMicrophoneActive = false;
}

// Helper class for Lexius custom RTVI messages
class LexiusRTVIHelper {
    constructor(audioCallHandler) {
        this.audioCallHandler = audioCallHandler; // Store reference to AudioCallHandler
        this.isSearching = false;
        this.searchQuery = '';
        this.searchResults = [];
        this.receivedMessages = [];
        this.errorMessage = null;
        // this.currentDisplayedResults = null; // Added to store a snapshot of current results

        /* — NEW — */
        this.searchHistory = [];   // [{ query, timestamp, results }]
        this.historyIndex = -1;   // where we currently are in that array

        /* -- DOCUMENT HISTORY -- */
        this.documentHistory = []; // [{ doc, timestamp }]
        this.documentHistoryIndex = -1; // Current index in documentHistory
    }

    handleMessage(rtviMessage) {
        console.log('[LexiusRTVIHelper] Received message:', rtviMessage);

        // Hide idle notice on any incoming message, as activity is implied.
        // If the incoming message IS an idle warning/termination, it will be re-displayed by its handler.
        this._hideIdleNotice();

        if (!rtviMessage || !rtviMessage.type || !rtviMessage.data) {
            console.warn('[LexiusRTVIHelper] Invalid message structure', rtviMessage);
            return;
        }

        console.log(`[LexiusRTVIHelper] Processing message type: ${rtviMessage.type}`);

        switch (rtviMessage.type) {
            case 'lexius_search_status':
                this.isSearching = true;
                this.searchQuery = rtviMessage.data.query || '';
                this.errorMessage = null; // Clear previous errors
                this._updateSearchStatus(true, this.searchQuery);
                break;
            case 'lexius_search_results':
                this.isSearching = false;

                const rawResults = rtviMessage.data.searchResults || [];
                const uniqueResults = [];
                const seenContents = new Set(); // Changed from seenIds to seenContents

                for (const result of rawResults) {
                    // Stringify the result object for content-based deduplication
                    // Sort keys before stringifying to ensure consistent representation
                    // for objects with same content but different key order.
                    const resultString = JSON.stringify(result, Object.keys(result).sort());

                    if (!seenContents.has(resultString)) {
                        uniqueResults.push(result);
                        seenContents.add(resultString);
                    } else {
                        // Log duplicated item if needed, for now, it's silently removed
                        console.log('[LexiusRTVIHelper] Duplicate search result item removed');
                    }
                }

                /* push onto the stack */
                const entry = {
                    query: rtviMessage.data.query || this.searchQuery,
                    timestamp: rtviMessage.data.timestamp || Date.now(),
                    results: uniqueResults, // Use the deduplicated results
                };
                this.searchHistory.push(entry);
                this.historyIndex = this.searchHistory.length - 1;

                this._updateSearchStatus(false, entry.query);
                this._renderHistory();
                break;
            case 'lexius_search_error':
                this.isSearching = false;
                this.errorMessage = rtviMessage.data.error || "Error durante la búsqueda.";
                this._displayError(this.errorMessage);
                break;
            case 'lexius_document_status':
                this._updateDocumentStatus(rtviMessage.data);
                break;
            case 'lexius_document_content':
                this._displayDocumentContent(rtviMessage.data);

                // --- Add to document history ---
                const docEntry = {
                    doc: rtviMessage.data.document,
                    timestamp: rtviMessage.data.timestamp || Date.now()
                };
                // Avoid adding duplicates based on document path or ID if available
                const existingDocIndex = this.documentHistory.findIndex(
                    entry => (entry.doc.path && entry.doc.path === docEntry.doc.path) || (entry.doc.id && entry.doc.id === docEntry.doc.id)
                );

                if (existingDocIndex !== -1) {
                    // If document already exists, update its timestamp and move to it
                    this.documentHistory[existingDocIndex].timestamp = docEntry.timestamp;
                    // Optionally, update content if it could change, though typically it shouldn't for same ID/path
                    this.documentHistory[existingDocIndex].doc = docEntry.doc;
                    this.documentHistoryIndex = existingDocIndex;
                    console.log('[LexiusRTVIHelper] Document already in history, updated and set as current.');
                } else {
                    // Add new document to history
                    this.documentHistory.push(docEntry);
                    this.documentHistoryIndex = this.documentHistory.length - 1;
                    console.log('[LexiusRTVIHelper] New document added to history.');
                }
                this._renderDocumentHistory();
                // --- End document history ---
                break;
            case 'lexius_document_error':
                this.errorMessage = rtviMessage.data.error || "Error al obtener el documento.";
                this._displayError(this.errorMessage);
                break;
            case 'lexius_idle_warning':
                this._displayIdleNotice(rtviMessage.data); // data contains title, message, level
                break;
            case 'lexius_idle_termination':
                this._displayIdleNotice(rtviMessage.data); // data contains title, message, level
                // The call will be terminated by the backend, leading to a disconnect event handled by AudioCallHandler
                break;
            case 'lexius_time_limit_warning':
                this._displayIdleNotice(rtviMessage.data); // data contains title, message, level
                break;
            case 'lexius_time_limit_termination':
                this._displayIdleNotice(rtviMessage.data); // data contains title, message, level
                // The call will be terminated by the backend, leading to a disconnect event handled by AudioCallHandler
                break;
            default:
                console.warn('[LexiusRTVIHelper] Unhandled message type:', rtviMessage.type);
        }
    }

    getMessageTypes() {
        return ['lexius_search_status', 'lexius_search_results', 'lexius_search_error', 'lexius_call_transcript_completed', 'lexius_idle_warning', 'lexius_idle_termination', 'lexius_time_limit_warning', 'lexius_time_limit_termination', 'lexius_document_status', 'lexius_document_content', 'lexius_document_error'];
    }

    // UI update helpers
    _updateSearchStatus(isSearching, query) {
        const searchStatusEl = document.getElementById('audio-call-search-status');
        const callStatusEl = document.getElementById('audio-call-status');

        let message = '';
        if (isSearching) {
            message = `Buscando información sobre '${query}'...`;
        }

        if (searchStatusEl) {
            if (isSearching) {
                searchStatusEl.innerHTML = `
                    <div class="text-blue-500 font-semibold flex items-center justify-center text-lg">
                        <div class="spinner-border spinner-border-sm text-blue-500 mr-2" role="status"></div>
                        ${message}
                    </div>`;
                searchStatusEl.style.display = 'block';
                if (callStatusEl) callStatusEl.style.display = 'none';
            } else {
                searchStatusEl.style.display = 'none';
                if (callStatusEl) callStatusEl.style.display = 'block';
            }
        }

        // Show top-screen alert if modal is not open and there's a message
        const audioCallModalEl = document.getElementById('audioCallModal');
        if (message && (!audioCallModalEl || !audioCallModalEl.classList.contains('show'))) {
            this.audioCallHandler._showTopScreenAlert(message, 'info', true);
        }
    }

    _hideIdleNotice() {
        const noticeEl = document.getElementById('audio-call-idle-notice');
        if (noticeEl && noticeEl.style.display !== 'none') {
            console.log('[LexiusRTVIHelper] Hiding idle notice.');
            noticeEl.style.display = 'none';
            noticeEl.innerHTML = '';

            // After hiding the idle notice, restore the correct primary status display
            // by calling _updateSearchStatus. It will show search status if a search
            // is active, or the general call status otherwise.
            this._updateSearchStatus(this.isSearching, this.searchQuery);
        }
    }

    _getSearchKeywords() {
        // Remove punctuation, split by whitespace, filter out short words
        if (!this.searchQuery) return [];
        return this.searchQuery
            .toLowerCase()
            .replace(/[.,\/#!$%\^&\*;:{}=\-_`~()¿?¡!"']/g, '')
            .split(/\s+/)
            .filter(word => word.length >= 3);
    }

    _highlightKeywords(text) {
        if (!text) return '';
        const keywords = this._getSearchKeywords();
        if (keywords.length === 0) return text;
        // Build a regex to match all keywords (word boundaries, case-insensitive)
        const regex = new RegExp(`\\b(${keywords.join('|')})\\b`, 'gi');
        return text.replace(regex, '<mark class="search-highlight">$1</mark>');
    }

    _displaySearchResults(results) {
        console.log("[LexiusRTVIHelper] _displaySearchResults called with results:", JSON.parse(JSON.stringify(results)));

        const jurisprudenceResultsContainer = document.getElementById('jurisprudence-results-container');
        const legislationResultsContainer = document.getElementById('legislation-results-container');
        const userDocumentResultsContainer = document.getElementById('user-document-results-container');

        const jurisprudenceTableJQuery = $('#jurisprudenceResultsTable');
        const jurisprudenceTableHead = document.getElementById('jurisprudenceResultsTableHead');
        const jurisprudenceTableBody = document.getElementById('jurisprudenceResultsTableBody');
        const jurisprudenceResultsCountEl = document.getElementById('jurisprudence-results-count');

        const legislationTableJQuery = $('#legislationResultsTable');
        const legislationTableHead = document.getElementById('legislationResultsTableHead');
        const legislationTableBody = document.getElementById('legislationResultsTableBody');
        const legislationResultsCountEl = document.getElementById('legislation-results-count');

        const userDocumentTableJQuery = $('#userDocumentResultsTable');
        const userDocumentTableHead = document.getElementById('userDocumentResultsTableHead');
        const userDocumentTableBody = document.getElementById('userDocumentResultsTableBody');
        const userDocumentResultsCountEl = document.getElementById('user-document-results-count');

        if (!jurisprudenceResultsContainer || !legislationResultsContainer || !userDocumentResultsContainer ||
            !jurisprudenceTableHead || !jurisprudenceTableBody || !jurisprudenceResultsCountEl ||
            !legislationTableHead || !legislationTableBody || !legislationResultsCountEl ||
            !userDocumentTableHead || !userDocumentTableBody || !userDocumentResultsCountEl) {
            console.error("[LexiusRTVIHelper] One or more search result table elements were not found");
            return;
        }

        // Filter results
        const jurisprudenceResults = results.filter(r => r.type === 'jurisprudence');
        const legislationResults = results.filter(r => r.type === 'law');
        const userDocumentResults = results.filter(r => r.type === 'user_document');

        // --- Helper function to process a single table (jurisprudence or legislation) ---
        const processTable = (tableType, specificResults, containerEl, tableJQueryEl, headEl, bodyEl, countEl) => {
            // 1. Destroy DataTable if it exists.
            if ($.fn.dataTable && tableJQueryEl.length > 0 && $.fn.dataTable.isDataTable(tableJQueryEl)) {
                console.log(`[LexiusRTVIHelper] Destroying existing DataTable for ${tableType}.`);
                tableJQueryEl.DataTable().destroy();
            }

            // 2. Clear the current HTML content of the table body and head.
            bodyEl.innerHTML = '';
            headEl.innerHTML = '';
            console.log(`[LexiusRTVIHelper] Table body and head cleared for ${tableType}.`);

            const tableElement = tableJQueryEl[0]; // Get the DOM element from jQuery object
            if (tableElement) {
                tableElement.classList.add('border', 'border-collapse', 'border-fourth');
            }

            if (specificResults && specificResults.length > 0) {
                console.log(`[LexiusRTVIHelper] Processing ${tableType} results. Count:`, specificResults.length);
                containerEl.style.display = 'block';
                if (countEl) {
                    countEl.textContent = specificResults.length;
                }

                // Define ordered columns based on table type
                let orderedColumns = [];
                if (tableType === 'jurisprudence') {
                    orderedColumns = ['tribunal', 'fecha', 'extract'];
                } else if (tableType === 'legislation') { // legislation
                    orderedColumns = ['nombre', 'fecha', 'extract'];
                } else if (tableType === 'user_document') {
                    orderedColumns = ['name', 'nombre', 'tipo de archivo', 'file_type', 'fecha', 'date', 'extract'];
                }

                // Get all available keys in the results
                const alwaysSpecial = ['type', 'extract', 'link', 'link_text', 'score', 'is_exact_match', 'id', 'display_id'];
                if (tableType === 'user_document') {
                    alwaysSpecial.push('description');
                }
                let allKeys = new Set();
                specificResults.forEach(r => {
                    Object.keys(r).forEach(k => {
                        if (!alwaysSpecial.includes(k) && r[k] !== null && r[k] !== undefined && String(r[k]).trim() !== '') {
                            allKeys.add(k);
                        }
                    });
                });

                // Create the final order of columns
                const orderedKeys = [];
                const findMatchingKey = (needle) => Array.from(allKeys).find(
                    existingKey => existingKey.toLowerCase() === String(needle).toLowerCase(),
                );

                // First add the specified ordered columns if they exist in the data
                for (const col of orderedColumns) {
                    const matchingKey = col !== 'extract' ? findMatchingKey(col) : null;
                    if (matchingKey) {
                        orderedKeys.push(matchingKey);
                        allKeys.delete(matchingKey);
                    }
                }

                // Then add any remaining keys
                const remainingKeys = Array.from(allKeys).sort();
                orderedKeys.push(...remainingKeys);

                // Create headers
                let headerHtml = '';
                orderedKeys.forEach(key => {
                    let label = key.replace(/_/g, ' ');
                    label = label.charAt(0).toUpperCase() + label.slice(1);
                    const normalizedKey = key.toLowerCase();

                    if (normalizedKey === 'name' || normalizedKey === 'nombre') {
                        label = 'Nombre';
                    } else if (normalizedKey === 'date' || normalizedKey === 'fecha') {
                        label = 'Fecha';
                    } else if (normalizedKey === 'court' || normalizedKey === 'tribunal') {
                        label = window.country === 'colombia' && specificResults.some(result => result.tipo_de_contenido)
                            ? 'Tribunal / entidad' : 'Tribunal';
                    } else if (normalizedKey === 'file_type' || normalizedKey === 'tipo de archivo') {
                        label = 'Tipo de archivo';
                    }

                    headerHtml += `<th class="px-4 py-3 border border-fourth">${label}</th>`;
                });

                // Add extract column header with responsive width
                headerHtml += `<th class="px-4 py-3 border border-fourth" style="min-width: 35%; max-width: 50%;">Extracto</th>`;
                headerHtml += `<th class="px-4 py-3 border border-fourth">Enlace</th>`;
                headEl.innerHTML = `<tr>${headerHtml}</tr>`;

                specificResults.forEach(result => {
                    const row = document.createElement('tr');
                    row.className = 'transition-colors duration-150 ease-in-out';

                    let dynamicCellsHtml = '';
                    orderedKeys.forEach(key => {
                        let value = result[key];
                        if (value === undefined || value === null) value = '';
                        if (typeof value === 'object') {
                            value = JSON.stringify(value);
                        }
                        dynamicCellsHtml += `<td class="px-4 py-3 border border-fourth max-h-[300px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">
                            <div class="whitespace-normal max-h-[280px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">${this._highlightKeywords(String(value))}</div>
                        </td>`;
                    });

                    // Enhanced extract cell with higher priority for responsive tables
                    let extractCellHtml = `
                        <td class="px-4 py-3 border border-fourth max-h-[300px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200" style="min-width: 35%; max-width: 50%;">
                            <div class="whitespace-normal max-h-[280px] overflow-y-auto scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-gray-200">${this._highlightKeywords(result.extract)}</div>
                        </td>
                    `;

                    let linkCellHtml = `
                        <td class="px-4 py-3 border border-fourth">
                            <a href="${result.link}"
                               rel="noopener noreferrer" 
                               title="Abrir ${result.link_text || 'documento'}"
                               class="inline-flex items-center px-3 py-1 rounded-full bg-theme-color text-white hover:bg-opacity-90 transition-colors font-medium">
                                <i class="anticon anticon-link mr-1"></i> Ver
                            </a>
                        </td>
                    `;
                    row.innerHTML = dynamicCellsHtml + extractCellHtml + linkCellHtml;
                    bodyEl.appendChild(row);
                });
                console.log(`[LexiusRTVIHelper] New rows appended to ${tableType} table body.`);

                if ($.fn.dataTable) {
                    console.log(`[LexiusRTVIHelper] Initializing DataTable for ${tableType}.`);

                    // Create columnDefs to configure responsive priority
                    const dynamicColumnCount = orderedKeys.length;
                    const extractColumnRenderedIndex = dynamicColumnCount;
                    const linkColumnRenderedIndex = dynamicColumnCount + 1;

                    let columnDefs = [
                        { // Common style for all cells
                            targets: '_all',
                            className: 'max-h-[300px] overflow-y-auto'
                        },
                        { // Specific style for Extract column - responsive width
                            targets: extractColumnRenderedIndex,
                            className: 'max-h-[300px] overflow-y-auto',
                            width: '35%'
                        }
                    ];
                    const findOrderedKeyIndex = (aliases) => orderedKeys.findIndex(
                        key => aliases.includes(String(key).toLowerCase()),
                    );

                    if (tableType === 'legislation') {
                        const nombreKeyCapIndexInOrderedKeys = findOrderedKeyIndex(['name', 'nombre']);
                        const fechaKeyCapIndexInOrderedKeys = findOrderedKeyIndex(['date', 'fecha']);

                        // Priority 1: Nombre (if it exists among the dynamic columns)
                        if (nombreKeyCapIndexInOrderedKeys !== -1) {
                            columnDefs.push({ targets: nombreKeyCapIndexInOrderedKeys, responsivePriority: 1 });
                        }
                        // Priority 2: Extracto (which is at extractColumnRenderedIndex)
                        columnDefs.push({ targets: extractColumnRenderedIndex, responsivePriority: 2 });
                        // Priority 3: Enlace (which is at linkColumnRenderedIndex)
                        columnDefs.push({ targets: linkColumnRenderedIndex, responsivePriority: 3 });
                        // Priority 4: Fecha (if it exists and is not 'nombre')
                        if (fechaKeyCapIndexInOrderedKeys !== -1 && fechaKeyCapIndexInOrderedKeys !== nombreKeyCapIndexInOrderedKeys) {
                            columnDefs.push({ targets: fechaKeyCapIndexInOrderedKeys, responsivePriority: 4 });
                        }

                        // Priority for other dynamic columns (those not 'nombre' or 'fecha')
                        let currentPriority = 5;
                        for (let i = 0; i < dynamicColumnCount; i++) {
                            if (i !== nombreKeyCapIndexInOrderedKeys && i !== fechaKeyCapIndexInOrderedKeys) {
                                columnDefs.push({ targets: i, responsivePriority: currentPriority++ });
                            }
                        }
                    } else if (tableType === 'user_document') {
                        const nombreKeyIndex = findOrderedKeyIndex(['name', 'nombre']);
                        const tipoKeyIndex = findOrderedKeyIndex(['file_type', 'tipo de archivo']);
                        const fechaKeyIndex = findOrderedKeyIndex(['date', 'fecha']);

                        if (nombreKeyIndex !== -1) {
                            columnDefs.push({ targets: nombreKeyIndex, responsivePriority: 1 });
                        }
                        columnDefs.push({ targets: extractColumnRenderedIndex, responsivePriority: 2 });
                        columnDefs.push({ targets: linkColumnRenderedIndex, responsivePriority: 3 });
                        if (fechaKeyIndex !== -1) {
                            columnDefs.push({ targets: fechaKeyIndex, responsivePriority: 4 });
                        }
                        if (tipoKeyIndex !== -1) {
                            columnDefs.push({ targets: tipoKeyIndex, responsivePriority: 5 });
                        }
                    } else { // Default logic for jurisprudence or other types
                        // Priority 1: Extracto
                        columnDefs.push({ targets: extractColumnRenderedIndex, responsivePriority: 1 });
                        // Priority 2: Enlace
                        columnDefs.push({ targets: linkColumnRenderedIndex, responsivePriority: 2 });

                        // Priority for the first few dynamic columns (if they exist)
                        if (dynamicColumnCount >= 1) { // First dynamic column
                            columnDefs.push({ targets: 0, responsivePriority: 3 });
                        }
                        if (dynamicColumnCount >= 2) { // Second dynamic column
                            columnDefs.push({ targets: 1, responsivePriority: 4 });
                        }
                        // For any other dynamic columns after the first two
                        let currentPriority = 5;
                        for (let i = 2; i < dynamicColumnCount; i++) {
                            columnDefs.push({ targets: i, responsivePriority: currentPriority++ });
                        }
                    }

                    try {
                        tableJQueryEl.DataTable({
                            "responsive": true,
                            "language": { "url": "/static/json/datatables_es-ES.json" },
                            "info": false,
                            "paging": false,
                            "ordering": false, // Disable user-triggered ordering
                            "columnDefs": columnDefs,
                        });
                    } catch (error) {
                        console.error(`[LexiusRTVIHelper] Error initializing DataTable for ${tableType}:`, error);
                    }
                    console.log(`[LexiusRTVIHelper] DataTable initialized for ${tableType}.`);
                } else {
                    console.warn("[LexiusRTVIHelper] $.fn.dataTable not available.");
                }
            } else {
                console.log(`[LexiusRTVIHelper] No ${tableType} results to display. Hiding container.`);
                containerEl.style.display = 'none';
                if (countEl) {
                    countEl.textContent = '0';
                }
            }
        };
        // --- End Helper Function ---

        // Process Jurisprudence Table
        processTable('jurisprudence', jurisprudenceResults, jurisprudenceResultsContainer, jurisprudenceTableJQuery, jurisprudenceTableHead, jurisprudenceTableBody, jurisprudenceResultsCountEl);

        // Process Legislation Table
        processTable('legislation', legislationResults, legislationResultsContainer, legislationTableJQuery, legislationTableHead, legislationTableBody, legislationResultsCountEl);

        // Process User Documents Table
        processTable('user_document', userDocumentResults, userDocumentResultsContainer, userDocumentTableJQuery, userDocumentTableHead, userDocumentTableBody, userDocumentResultsCountEl);

        // this.currentDisplayedResults = newResultsString;
    }

    _displayIdleNotice(data) { // data: { title, message, level }
        const noticeEl = document.getElementById('audio-call-idle-notice');
        const callStatusEl = document.getElementById('audio-call-status');
        const searchStatusEl = document.getElementById('audio-call-search-status');

        if (noticeEl) {
            let baseColor = "gray";
            let icon = 'anticon-info-circle';

            if (data.level === 'warning') {
                baseColor = "yellow";
                icon = 'anticon-warning';
            } else if (data.level === 'info') { // For termination message
                baseColor = "blue";
            }

            // Using Tailwind-like classes for consistency with the project's theme if applicable, or inline styles.
            // Assuming utility classes like text-yellow-700, bg-yellow-100, border-yellow-500 are available or similar exist.
            noticeEl.innerHTML = `
                <div class="border-l-4 border-${baseColor}-500 bg-${baseColor}-100 text-${baseColor}-700 p-4 shadow-md rounded-md" role="alert">
                    <div class="flex">
                        <div class="py-1"><i class="anticon ${icon} text-${baseColor}-500 text-2xl mr-3"></i></div>
                        <div>
                            ${data.title ? `<p class="font-bold text-lg">${data.title}</p>` : ''}
                            <p class="text-sm">${data.message}</p>
                        </div>
                    </div>
                </div>
            `;
            noticeEl.style.display = 'block';

            // Hide other status messages like "Call in progress..." or "Searching..." to give focus to idle notice
            if (callStatusEl) callStatusEl.style.display = 'none';
            if (searchStatusEl) searchStatusEl.style.display = 'none';

        } else {
            console.warn("[LexiusRTVIHelper] audio-call-idle-notice element not found.");
        }
    }

    _displayError(message) {
        const errorMessageEl = document.getElementById('audio-call-error');
        if (errorMessageEl) {
            errorMessageEl.textContent = message;
            errorMessageEl.style.display = 'block';
            setTimeout(() => {
                errorMessageEl.style.display = 'none';
            }, 5000);
        }

        // Show top-screen alert if modal is not open
        const audioCallModalEl = document.getElementById('audioCallModal');
        if (!audioCallModalEl || !audioCallModalEl.classList.contains('show')) {
            this.audioCallHandler._showTopScreenAlert(message, 'danger', false);
        }
    }

    _extractTitle(linkText) {
        if (!linkText) return 'N/A';
        const match = linkText.match(/^Ver\\s+(.+?)(?:,\\s+|\\.\\s+|$)/);
        return match ? match[1] : linkText;
    }

    addMessage(message) {
        this.receivedMessages.push(message);
        this._updateMessages();
    }

    clearMessages() {
        this.receivedMessages = [];
        this._updateMessages();
    }

    _updateMessages() {
        const messagesContainer = document.getElementById('audio-call-messages');
        if (!messagesContainer) return;

        const callHandler = this.audioCallHandler;

        if (this.receivedMessages.length === 0) {
            if (callHandler && (callHandler.isCallActive || callHandler.isConnecting)) {
                // Call is active or connecting, and no messages yet: hide the initial prompt.
                messagesContainer.innerHTML = '';
            } else {
                // No call active/connecting, and no messages: show the initial prompt.
                messagesContainer.innerHTML = `
                <div class="h-full flex items-center justify-center text-md text-gray-400 dynamic-color">
                    <span>Inicie la llamada para comenzar.</span>
                </div>`;
            }
        } else {
            // We have messages, display them.
            messagesContainer.innerHTML = this.receivedMessages.map(msg => `<div class="mb-2 break-words">${msg}</div>`).join('');
            messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }
    }

    async _saveCallTranscriptToServer(session) {
        const body = JSON.stringify({
            uuid: session.uuid,
            title: session.title,
            messages: session.data,
            path: session.path,
            page_title: session.page_title,
            is_user_document: session.is_user_document,
            is_multi_document: session.is_multi_document,
            is_law: session.is_law,
        });

        // Reuse the same UUID and snapshot when retrying an uncertain server save.
        for (let attempt = 0; attempt < 3; attempt++) {
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 10000);
            try {
                const response = await fetch('/calls/save-transcript', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body,
                    signal: controller.signal,
                });
                if (response.ok) return true;
                console.warn('[LexiusRTVIHelper] Server transcript save failed:', response.status);
                if (response.status < 500 && ![408, 429].includes(response.status)) return false;
            } catch (error) {
                console.warn('[LexiusRTVIHelper] Server transcript save unavailable:', error.name);
            } finally {
                clearTimeout(timeout);
            }
            if (attempt < 2) {
                await new Promise(resolve => setTimeout(resolve, 500 * (attempt + 1)));
            }
        }
        return false;
    }

    _cacheCallTranscript(session) {
        try {
            const key = 'lexius_virtual_lawyer_chats';
            const chats = JSON.parse(localStorage.getItem(key) ?? '{}');
            // Preserve unreadable or malformed history: it may still await migration.
            if (!chats || typeof chats !== 'object' || Array.isArray(chats)) return false;
            chats[session.uuid] = session;
            localStorage.setItem(key, JSON.stringify(chats));
            return true;
        } catch (error) {
            console.warn('[LexiusRTVIHelper] Local transcript copy unavailable:', error.name);
            return false;
        }
    }

    async _handleCallTranscript(transcriptData, context = null) {

        if (!Array.isArray(transcriptData?.messages) || transcriptData.messages.length === 0) {
            console.warn("[LexiusRTVIHelper] Empty or invalid transcript data received. Aborting transcript processing.");
            return;
        }

        // Process transcript messages: handle system messages from function handlers
        // (like search results, jurisprudence, legislation) differently from user/bot messages
        const callMessages = transcriptData.messages.map(msg => {
            // The backend sends role: "user", "assistant", or "system"
            // Frontend ChatLayout expects sender: "user", "bot", or "system"

            if (msg.role === 'system') {
                // Handle system messages with proper formatting for SystemMessage component
                // These come from function handlers in pipecat_bot.py (buscar, obtener_jurisprudencia, etc.)
                return {
                    content: msg.content, // Use 'content' instead of 'text' for system messages
                    sender: 'system',
                    timestamp: new Date(msg.timestamp).getTime() // Ensure timestamp is a number
                };
            } else {
                // Handle regular user/bot messages
                const sender = msg.role === 'user' ? 'user' : 'bot';
                return {
                    text: msg.content, // Use 'text' for regular messages
                    sender: sender,
                    timestamp: new Date(msg.timestamp).getTime() // Ensure timestamp is a number
                };
            }
        });

        // Generate a title for the call log
        const callStartTime = callMessages.length > 0 ? new Date(callMessages[0].timestamp) : new Date();
        const formattedDate = `${callStartTime.toLocaleDateString()} ${callStartTime.toLocaleTimeString()}`;
        const sessionTitle = `Llamada - ${formattedDate}`;

        // Default session fields
        let path = null;
        let page_title = null;
        let is_user_document = false;
        let is_law = false;
        let is_multi_document = false;
        let page_title_html = null;

        // If context is provided and non-empty, populate fields for context display
        if (Array.isArray(context) && context.length > 0) {
            if (context.length === 1) {
                // Single document context
                path = context[0].path;
                page_title = context[0].title;
                is_user_document = context[0].is_user_document || false;
                is_law = context[0].is_law || false;
                is_multi_document = false;
                page_title_html = null;
            } else {
                // Multi-document context
                path = context.map(doc => doc.path);
                page_title = context.map(doc => doc.title);
                is_user_document = context.map(doc => doc.is_user_document || false);
                is_law = context.some(doc => doc.is_law); // true if any doc is law
                is_multi_document = true;
                // Generate HTML for document list
                page_title_html = "<ol style='margin-left:2%;'>";
                for (let i = 0; i < path.length; i++) {
                    page_title_html += `<li><a href='${path[i]}' class='text-secondary font-bold text-center'>${page_title[i]}</a></li>`;
                }
                page_title_html += "</ol>";
            }
        }

        const newSession = {
            uuid: `call-${Date.now()}`,
            title: sessionTitle,
            data: callMessages,
            created_at: callStartTime.getTime(),
            updated_at: callMessages.length > 0 ? callMessages[callMessages.length - 1].timestamp : callStartTime.getTime(),
            path: path,
            page_title: page_title,
            is_user_document: is_user_document,
            is_law: is_law,
            is_multi_document: is_multi_document,
            page_title_html: page_title_html,
            suggestedQuestions: [], // No suggested questions for call logs
            is_call_log: true // Special flag for call logs
        };

        const syncEnabled = window.sync_enabled === true;
        const savedToServer = syncEnabled && await this._saveCallTranscriptToServer(newSession);
        // Synchronized calls do not grow the legacy localStorage history. Only
        // local-only calls or a failed server save need a best-effort local copy.
        const savedLocally = !savedToServer && this._cacheCallTranscript(newSession);
        // Keep pending status in this tab so cached flags cannot outlive a
        // later server confirmation or deletion.
        if (!savedToServer && (syncEnabled || !savedLocally)) {
            newSession.call_transcript_pending_save = true;
        }

        // Carry the transcript itself so viewing it never requires a cache write
        // or a successful follow-up GET. Refresh lists only after the save settles.
        window.dispatchEvent(new CustomEvent('calltranscriptadded', {
            detail: { sessionId: newSession.uuid, sessionTitle: newSession.title, session: newSession }
        }));

        if (syncEnabled && !savedToServer) {
            alert(savedLocally
                ? 'No se pudo sincronizar la transcripción de la llamada. Se guardó en este dispositivo. Cuando vuelva la conexión, repite la migración desde Configuración para subirla a la nube.'
                : 'No se pudo sincronizar la transcripción de la llamada. Sigue disponible en esta pestaña. Mantén la pestaña abierta y exporta la transcripción antes de cerrarla.');
        } else if (!syncEnabled && !savedLocally) {
            alert('No se pudo guardar la transcripción en este dispositivo. Sigue disponible en esta pestaña; expórtala antes de cerrarla. Activa la sincronización para guardar futuras llamadas en la nube.');
        }

        try {
            const audioCallModalEl = document.getElementById('audioCallModal');
            if (audioCallModalEl) {
                const bsAudioModal = bootstrap.Modal.getInstance(audioCallModalEl);
                if (bsAudioModal) {
                    // Listen for the modal to be fully hidden before showing the chat modal
                    audioCallModalEl.addEventListener('hidden.bs.modal', () => {
                        const chatModalEl = document.getElementById('chatModal');
                        if (chatModalEl) {
                            const bsChatModal = bootstrap.Modal.getInstance(chatModalEl) || new bootstrap.Modal(chatModalEl);
                            // Ensure chatModal is not already shown or in the process of showing
                            if (bsChatModal && !chatModalEl.classList.contains('show')) {
                                // Show the chat modal
                                bsChatModal.show();
                                console.log("[LexiusRTVIHelper] Main chat modal shown after audio modal hidden.");

                                // Wait for the chat modal to be fully shown before processing
                                chatModalEl.addEventListener('shown.bs.modal', () => {
                                    // Additional delay to ensure DOM is fully ready
                                    setTimeout(() => {
                                        console.log("[LexiusRTVIHelper] Chat modal fully shown, DOM should be ready for DataTables initialization.");
                                    }, 100);
                                }, { once: true });
                            } else if (!bsChatModal) {
                                console.warn("[LexiusRTVIHelper] Bootstrap instance for chatModal not found for showing.");
                            }
                        } else {
                            console.warn("[LexiusRTVIHelper] chatModal element not found for showing.");
                        }
                    }, { once: true }); // Ensure the listener is called only once
                    console.log("[LexiusRTVIHelper] Audio call modal hide initiated.");

                } else {
                    console.warn("[LexiusRTVIHelper] Bootstrap instance for audioCallModal not found for hiding.");
                }
            } else {
                console.warn("[LexiusRTVIHelper] audioCallModal element not found for hiding.");
            }

        } catch (e) {
            console.warn('[LexiusRTVIHelper] Could not show the transcript modal:', e);
        }
        return newSession;
    }

    _updateDocumentStatus(data) {
        const documentStatusEl = document.getElementById('audio-call-document-status');
        const callStatusEl = document.getElementById('audio-call-status');

        if (!documentStatusEl) {
            // Create the status element if it doesn't exist
            // Using a container that exists in the HTML structure
            const statusContainer = document.querySelector('.modal-body .w-100');
            if (statusContainer) {
                const newStatusEl = document.createElement('div');
                newStatusEl.id = 'audio-call-document-status';
                newStatusEl.className = 'text-center my-3';
                statusContainer.insertBefore(newStatusEl, statusContainer.firstChild);
            } else {
                console.warn('[LexiusRTVIHelper] Could not find container for document status');
                return;
            }
        }

        // Now we can be sure documentStatusEl exists
        const documentStatusElement = document.getElementById('audio-call-document-status');

        if (documentStatusElement) {
            // Safely set the message text
            const messageContainer = document.createElement('div');
            messageContainer.className = "text-blue-500 font-semibold flex items-center justify-center text-lg"; // Note: Corrected class attribute quote

            const spinner = document.createElement('div');
            spinner.className = "spinner-border spinner-border-sm text-blue-500 mr-2";
            spinner.setAttribute('role', 'status');

            const messageTextSpan = document.createElement('span'); // Use a span for the text
            messageTextSpan.textContent = data.message; // Set as text content

            messageContainer.appendChild(spinner);
            messageContainer.appendChild(messageTextSpan);

            documentStatusElement.innerHTML = ''; // Clear previous content
            documentStatusElement.appendChild(messageContainer); // Append the new, safe content

            documentStatusElement.style.display = 'block';

            if (callStatusEl) callStatusEl.style.display = 'none';
        }

        // Show top-screen alert if modal is not open
        const audioCallModalEl = document.getElementById('audioCallModal');
        if (!audioCallModalEl || !audioCallModalEl.classList.contains('show')) {
            this.audioCallHandler._showTopScreenAlert(data.message, 'info', true);
        }
    }

    _displayDocumentContent(data) {
        // Hide the document status
        const documentStatusEl = document.getElementById('audio-call-document-status');
        if (documentStatusEl) {
            documentStatusEl.style.display = 'none';
        }

        // Show the call status again
        const callStatusEl = document.getElementById('audio-call-status');
        if (callStatusEl) {
            callStatusEl.style.display = 'block';
        }

        const doc = data.document;
        if (!doc) {
            console.warn('[LexiusRTVIHelper] No document data provided');
            return;
        }

        // Create document display panel
        this._createOrUpdateDocumentPanel(doc);

        // Show top pop-up message when the AI has checked the full text
        const audioCallModalEl = document.getElementById('audioCallModal');
        // Only show if modal is not open (to avoid duplicate/confusing alerts)
        if (!audioCallModalEl || !audioCallModalEl.classList.contains('show')) {
            this.audioCallHandler._showTopScreenAlert(
                'El Abogado Virtual ha revisado el texto completo de un documento.',
                'info',
                true
            );
        }
    }

    _createOrUpdateDocumentPanel(doc) {
        // Find or create a container for the document
        let documentPanelContainer = document.getElementById('document-content-container');

        const docType = doc.type;
        const docTitle = docType === 'jurisprudence' ?
            'Jurisprudencia Analizada' :
            'Legislación Analizada';

        const docIconClass = docType === 'jurisprudence' ?
            'anticon-audit' :
            'anticon-book';

        // Format metadata for display
        let metadataHtml = '';
        if (doc.metadata && typeof doc.metadata === 'object') {
            metadataHtml = '<div class="metadata-section p-4 border-b border-theme-color border-opacity-20">';
            metadataHtml += '<h5 class="font-semibold mb-3">Metadatos</h5>';
            metadataHtml += '<dl class="grid grid-cols-1 md:grid-cols-2 gap-2">';

            for (const [key, value] of Object.entries(doc.metadata)) {
                // Ensure value is not null/undefined and, when converted to string, is not empty after trimming
                if (value !== null && typeof value !== 'undefined' && String(value).trim() !== '') {
                    // Create temporary DOM elements to safely set text content
                    const tempDt = document.createElement('dt');
                    tempDt.className = "font-medium text-theme-color";
                    // Assuming 'key' itself is a simple string and doesn't need escaping for display.
                    // If 'key' could also be problematic, it should also be set via textContent.
                    tempDt.textContent = key + ":";

                    const tempDd = document.createElement('dd');
                    tempDd.textContent = String(value); // Ensure value is treated as plain text

                    metadataHtml += `
                        <div class="metadata-item mb-2">
                            ${tempDt.outerHTML}
                            ${tempDd.outerHTML}
                        </div>
                    `;
                }
            }

            metadataHtml += '</dl></div>';
        }

        // Main document content
        const contentHtml = doc.path ?
            `<div class="content-section p-4 text-center">
                <a href="${doc.path}" class="btn btn-primary btn-lg">
                    <i class="anticon anticon-file-text me-2"></i>Abrir Documento Completo
                </a>
            </div>` :
            '<div class="content-section p-4 text-center">No se pudo obtener el enlace del documento.</div>';

        // Assemble the complete panel
        documentPanelContainer.innerHTML = `
            <div class="bg-fourth rounded-lg overflow-hidden shadow-lg border border-theme-color border-opacity-30 mb-5">
                <div class="p-4 bg-theme-color bg-opacity-10 border-b border-theme-color border-opacity-20">
                    <h4 class="fs-4 fw-semibold d-flex align-items-center text-primary">
                        <i class="anticon ${docIconClass} me-2"></i>
                        ${docTitle}
                    </h4>
                    <div class="text-sm text-gray-600 mt-1">
                        ${docType === 'jurisprudence' ?
                `ID: ${doc.id}` :
                `Ruta: ${doc.path}`}
                    </div>
                </div>
                ${metadataHtml}
                ${contentHtml}
            </div>
        `;

        // Scroll to the document panel
        console.log("[LexiusRTVIHelper] Scrolling to document panel");
        documentPanelContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });

    }

    _renderHistory() {
        const entry = this.searchHistory[this.historyIndex];
        if (!entry) return;

        /* reuse your existing table‑builder */
        this._displaySearchResults(entry.results);

        /* update small nav */
        this._updateSearchNavigator(entry);
    }

    _updateSearchNavigator(entry) {
        const nav = document.getElementById('search-history-nav');
        const label = document.getElementById('search-history-label');
        const prevBtn = document.getElementById('prev-search-btn');
        const nextBtn = document.getElementById('next-search-btn');

        /* first run: attach click handlers */
        if (prevBtn && !prevBtn.onclick) {
            prevBtn.onclick = () => {
                if (this.historyIndex > 0) {
                    this.historyIndex--;
                    this._renderHistory();
                }
            };
        }
        if (nextBtn && !nextBtn.onclick) {
            nextBtn.onclick = () => {
                if (this.historyIndex < this.searchHistory.length - 1) {
                    this.historyIndex++;
                    this._renderHistory();
                }
            };
        }

        if (label) {
            // To prevent overly long labels, truncate the query if necessary
            const queryText = entry.query.length > 50 ? entry.query.substring(0, 47) + "..." : entry.query;
            label.innerHTML = `<span class="search-query-text" title="${entry.query}">Resultados para "${queryText}"</span> <span class="search-counter">(${this.historyIndex + 1}/${this.searchHistory.length})</span>`;
        }
        if (prevBtn)
            prevBtn.disabled = this.historyIndex === 0;
        if (nextBtn)
            nextBtn.disabled = this.historyIndex === this.searchHistory.length - 1;
        if (nav)
            nav.style.display = this.searchHistory.length > 1 ? 'flex' : 'none';
    }

    _renderDocumentHistory() {
        const entry = this.documentHistory[this.documentHistoryIndex];
        if (!entry) return;

        this._createOrUpdateDocumentPanel(entry.doc);
        this._updateDocumentNavigator(entry);
    }

    _updateDocumentNavigator(entry) {
        const nav = document.getElementById('document-history-nav');
        const label = document.getElementById('document-history-label');
        const prevBtn = document.getElementById('prev-document-btn');
        const nextBtn = document.getElementById('next-document-btn');

        if (!nav || !label || !prevBtn || !nextBtn) {
            console.warn('[LexiusRTVIHelper] Document history navigation elements not found.');
            return;
        }

        /* first run: attach click handlers */
        if (!prevBtn.onclick) {
            prevBtn.onclick = () => {
                if (this.documentHistoryIndex > 0) {
                    this.documentHistoryIndex--;
                    this._renderDocumentHistory();
                }
            };
        }
        if (!nextBtn.onclick) {
            nextBtn.onclick = () => {
                if (this.documentHistoryIndex < this.documentHistory.length - 1) {
                    this.documentHistoryIndex++;
                    this._renderDocumentHistory();
                }
            };
        }

        let docIdentifier = entry.doc.id || entry.doc.path?.split('/').pop() || 'Documento';
        if (docIdentifier.length > 40) docIdentifier = docIdentifier.substring(0, 37) + "...";

        label.innerHTML = `<span class="document-title-text" title="${entry.doc.id || entry.doc.path || 'Documento'}">Documento: "${docIdentifier}"</span> <span class="document-counter">(${this.documentHistoryIndex + 1}/${this.documentHistory.length})</span>`;

        prevBtn.disabled = this.documentHistoryIndex === 0;
        nextBtn.disabled = this.documentHistoryIndex === this.documentHistory.length - 1;
        nav.style.display = this.documentHistory.length > 1 ? 'flex' : 'none';
    }
}

class AudioCallHandler {
    constructor() {
        this.isCallActive = false;
        this.isConnecting = false;
        this.callStartTime = null;
        this.elapsedTime = 0;
        this.timerInterval = null;
        this.rtviClient = null;
        this.dailyCallClient = null;
        this.callAttemptId = 0;
        this.botAudioElement = null;
        this.helper = new LexiusRTVIHelper(this); // Pass 'this' (AudioCallHandler instance)
        this.lexiusServerUrl = "https://voiceapi.lexius.io";

        if (window.environment == "development") {
            this.lexiusServerUrl = "https://voiceapidev.lexius.io";
        }
        else if (window.environment == "beta") {
            this.lexiusServerUrl = "https://voiceapibeta.lexius.io";
        }

        this.flaskServerUrl = ""; // Will be set dynamically
        this.callVoiceList = []; // Cached voices for call selection
        this.callVoicePreviewAudio = null;
        this.currentRoomUrl = null; // Added to store the room URL
        this.processingTermination = false; // Add this line
        this.chatModalButtonListenerAttached = false; // Flag to ensure listener is attached only once
        this.currentCallContext = null; // Store the context for the current call
        this.topAlertTimeout = null; // For managing top screen alert dismissal
        // Bind context handling methods
        this._handleContextConfirmation = this._handleContextConfirmation.bind(this);
        this._handleRemoveContextDocument = this._handleRemoveContextDocument.bind(this);
        this._handleDailyAppMessage = this._handleDailyAppMessage.bind(this);
    }

    _handleDailyAppMessage(event) {
        const message = event?.data;
        if (
            message?.label !== 'rtvi-ai'
            || !this.helper.getMessageTypes().includes(message.type)
        ) {
            return;
        }

        this.helper.handleMessage(message);
    }

    _createDailyTransport(options) {
        return new DailyTransport(options);
    }

    _createPipecatClient(options) {
        return new PipecatClient(options);
    }

    _setMicrophoneOwnership(active) {
        if (window.lexiusCallMicrophoneActive === active) return;
        window.lexiusCallMicrophoneActive = active;
        // Dispatch synchronously so dictation releases its tracks before Daily
        // can request the microphone, including during call connection setup.
        window.dispatchEvent(new CustomEvent('lexius:microphone-call', {
            detail: { active },
        }));
    }

    async _deleteRoomBestEffort(roomUrl) {
        if (!roomUrl) return;

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        try {
            const response = await fetch(`${this.lexiusServerUrl}/delete_room`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ room_url: roomUrl }),
                signal: controller.signal,
            });
            if (!response.ok) {
                this._log(`[Warning] Could not delete canceled Daily room: ${response.status}`);
            }
        } catch (error) {
            this._log(`[Warning] Could not delete canceled Daily room: ${error?.message || error}`);
        } finally {
            clearTimeout(timeoutId);
        }
    }

    async _disconnectPipecatClientBestEffort(pipecatClient) {
        if (!pipecatClient || ['disconnected', 'disconnecting'].includes(pipecatClient.state)) {
            return;
        }

        let timeoutId;
        const disconnectResult = Promise.resolve()
            .then(() => pipecatClient.disconnect())
            .then(() => ({ completed: true }))
            .catch(error => ({ completed: true, error }));
        const timeoutResult = new Promise(resolve => {
            timeoutId = setTimeout(() => resolve({ completed: false }), 5000);
        });
        const result = await Promise.race([disconnectResult, timeoutResult]);
        clearTimeout(timeoutId);

        if (!result.completed) {
            this._log('[Warning] Timed out disconnecting Pipecat client. Continuing cleanup.');
        } else if (result.error) {
            this._log(`[Warning] Could not disconnect Pipecat client: ${result.error?.message || result.error}`);
        }
    }

    initialize() {
        console.log("[AudioCallHandler] Initializing");

        // Dynamically set flaskServerUrl based on current window origin
        this.flaskServerUrl = window.location.origin;
        // Load available voices for calls
        this._loadVoicesForCalls();
        console.log(`[AudioCallHandler] Flask server URL set to: ${this.flaskServerUrl}`);

        // Set up audio element
        this.botAudioElement = document.getElementById('botAudioOutput');
        if (!this.botAudioElement) {
            console.error("Bot audio output element not found!");
        }

        // Update and attach event handlers for context UI
        this._updateContextUI();
        this._attachContextEventHandlers();

        // Set up UI (general call UI)
        this._updateUI();

        // Attach event handlers (general call event handlers)
        this._attachEventHandlers();

        // Attach listener to the main chat modal button if not already attached
        if (!this.chatModalButtonListenerAttached) {
            const chatModalButton = document.querySelector('.chat-modal');
            if (chatModalButton) {
                chatModalButton.addEventListener('click', this._handleChatModalButtonClick.bind(this), true); // Use capturing phase
                this.chatModalButtonListenerAttached = true;
                console.log("[AudioCallHandler] Event listener attached to .chat-modal button.");
            } else {
                console.warn("[AudioCallHandler] .chat-modal button not found for attaching listener.");
            }
        }

        // Add visual cue to the main chat button if call is active
        const chatModalButton = document.querySelector('.chat-modal');
        if (chatModalButton && (this.isCallActive || this.isConnecting)) {
            chatModalButton.classList.add('audio-call-active-indicator');
        }
    }
    async _loadVoicesForCalls() {
        const selectionRevision = this.callVoiceSelectionRevision || 0;
        try {
            const res = await fetch(`${this.flaskServerUrl}/get-voices`, { method: 'GET' });
            if (!res.ok) {
                if (res.status === 401) {
                    handleSessionExpired('la Llamada de Voz');
                }
                throw new Error('No se pudieron cargar las voces');
            }
            const data = await res.json();
            if (window.LexiusVoicePreferences) await window.LexiusVoicePreferences.ready;
            this.callVoicesData = data;
            const voiceAccount = [window.country, window.user_id || window.user_email].join(':');
            if (this.callVoiceAccount !== voiceAccount && !this.isCallActive && !this.isConnecting) {
                const hidden = document.getElementById('callVoiceSelect');
                if (hidden) hidden.value = '';
            }
            this.callVoiceAccount = voiceAccount;
            if (!this.isCallActive && !this.isConnecting && selectionRevision === (this.callVoiceSelectionRevision || 0)) {
                const hidden = document.getElementById('callVoiceSelect');
                if (hidden && window.LexiusVoicePreferences) hidden.value = window.LexiusVoicePreferences.get('calls');
            }
            this.callVoiceList = this._buildFlatVoiceList(data);
            this._initCallVoicePicker(data);
        } catch (e) {
            console.error('[AudioCallHandler] Error al cargar voces para llamadas:', e);
            const label = document.getElementById('callVoiceSelectedLabel');
            if (label) { label.textContent = 'No disponible'; }
            const openBtn = document.getElementById('openCallVoicePickerBtn');
            if (openBtn) { openBtn.disabled = true; }
            const previewBtn = document.getElementById('previewCallVoiceBtn');
            if (previewBtn) { previewBtn.disabled = true; }
        }
    }

    _buildFlatVoiceList(data) {
        const list = [];
        if (!data) return list;
        const { country_voices = [], default_library = [], defaults = {}, other_countries = [] } = data;
        // Defaults
        if (defaults && defaults.male && defaults.male.id) {
            // Include flag (if provided) together with gender for default voice names
            const maleLabel = defaults.male.label || 'Por defecto';
            const maleGender = 'Masculina';
            const maleCore = `${maleLabel} (${maleGender})`;
            const maleName = defaults.male.flag ? `${defaults.male.flag} ${maleCore}` : maleCore;
            list.push({ id: defaults.male.id, name: maleName, gender: 'male', sample_url: defaults.male.sample_url || '', flag: defaults.male.flag || '' });
        }
        if (defaults && defaults.female && defaults.female.id) {
            const femaleLabel = defaults.female.label || 'Por defecto';
            const femaleGender = 'Femenina';
            const femaleCore = `${femaleLabel} (${femaleGender})`;
            const femaleName = defaults.female.flag ? `${defaults.female.flag} ${femaleCore}` : femaleCore;
            list.push({ id: defaults.female.id, name: femaleName, gender: 'female', sample_url: defaults.female.sample_url || '', flag: defaults.female.flag || '' });
        }
        // Country
        country_voices.forEach(v => list.push({ id: v.id, name: `${v.flag || ''} ${v.name}`.trim(), gender: v.gender, sample_url: v.sample_url }));
        // Library
        default_library.forEach(v => list.push({ id: v.id, name: v.name, gender: v.gender, sample_url: v.sample_url }));
        // Other countries
        other_countries.forEach(country => {
            if (country && Array.isArray(country.voices)) {
                country.voices.forEach(v => list.push({ id: v.id, name: `${v.flag || ''} ${v.name}`.trim(), gender: v.gender, sample_url: v.sample_url }));
            }
        });
        return list;
    }

    _renderCallVoiceSelect(data) { // Backwards compatibility wrapper
        this._initCallVoicePicker(data);
    }

    _getCallVoiceById(id) {
        if (!id) return null;
        const list = this.callVoiceList || [];
        const sid = String(id);
        for (let i = 0; i < list.length; i++) {
            if (String(list[i].id) === sid) return list[i];
        }
        return null;
    }

    _initCallVoicePicker(data) {
        const hidden = document.getElementById('callVoiceSelect');
        const label = document.getElementById('callVoiceSelectedLabel');
        const openBtn = document.getElementById('openCallVoicePickerBtn');
        const previewBtn = document.getElementById('previewCallVoiceBtn');
        if (!hidden || !openBtn || !previewBtn || !data) return;

        const updatePreviewState = () => {
            const v = this._getCallVoiceById(hidden.value);
            if (label) label.textContent = v ? v.name : 'Sin seleccionar';
            if (v && v.sample_url) {
                previewBtn.disabled = false;
                previewBtn.onclick = () => {
                    const isPlaying = previewBtn.getAttribute('data-playing') === 'true';
                    if (isPlaying) {
                        this._stopCallVoicePreview();
                    } else {
                        this._playCallVoicePreview(v.sample_url, previewBtn);
                    }
                };
            } else {
                previewBtn.disabled = true;
                previewBtn.onclick = null;
            }
        };

        // Preselect a default voice if none is chosen yet
        try {
            const defaults = (data && data.defaults) ? data.defaults : {};
            if (!hidden.value || String(hidden.value).trim() === '') {
                const preferredVoice = window.LexiusVoicePreferences && window.LexiusVoicePreferences.get('calls');
                if (preferredVoice) {
                    hidden.value = preferredVoice;
                } else if (defaults.male && defaults.male.id) {
                    hidden.value = String(defaults.male.id);
                } else if (defaults.female && defaults.female.id) {
                    hidden.value = String(defaults.female.id);
                }
            }
        } catch (e) { /* noop */ }

        openBtn.disabled = false;
        openBtn.onclick = () => {
            if (!window.voicePicker) return;
            window.voicePicker.open({
                title: 'Selecciona una voz para la llamada',
                voicesData: data,
                gender: 'any',
                selectedVoiceId: hidden.value || null,
                onSelect: (voice) => {
                    this.callVoiceSelectionRevision = (this.callVoiceSelectionRevision || 0) + 1;
                    hidden.value = voice.id;
                    updatePreviewState();
                }
            });
        };

        updatePreviewState();
    }

    _playCallVoicePreview(sampleUrl, btnEl) {
        this._stopCallVoicePreview();
        if (window.LexiusResponseAudio) window.LexiusResponseAudio.pause();
        const audio = new Audio(sampleUrl);
        audio.volume = 1.0;
        audio.play().catch(err => console.error('Error playing call voice sample:', err));
        this.callVoicePreviewAudio = audio;
        btnEl.setAttribute('data-playing', 'true');
        btnEl.innerHTML = '<i class="anticon anticon-pause"></i>';
        audio.addEventListener('ended', () => this._stopCallVoicePreview());
    }

    _resetCallVoiceDefault() {
        if (this.isCallActive || this.isConnecting || !window.LexiusVoicePreferences) return;
        const hidden = document.getElementById('callVoiceSelect');
        if (!hidden) return;
        this.callVoiceSelectionRevision = (this.callVoiceSelectionRevision || 0) + 1;
        hidden.value = window.LexiusVoicePreferences.get('calls');
        if (this.callVoicesData) this._initCallVoicePicker(this.callVoicesData);
    }

    _stopCallVoicePreview() {
        const btnEl = document.getElementById('previewCallVoiceBtn');
        if (this.callVoicePreviewAudio) {
            this.callVoicePreviewAudio.pause();
            this.callVoicePreviewAudio = null;
        }
        if (btnEl) {
            btnEl.setAttribute('data-playing', 'false');
            btnEl.innerHTML = '<i class="anticon anticon-sound"></i>';
        }
    }

    _handleChatModalButtonClick(event) {
        if (this.isCallActive || this.isConnecting) {
            console.log("[AudioCallHandler] Call is active. Intercepting .chat-modal click.");
            event.preventDefault();
            event.stopPropagation();

            // Ensure main chat modal is hidden
            const chatModalEl = document.getElementById('chatModal');
            if (chatModalEl) {
                const bsChatModal = bootstrap.Modal.getInstance(chatModalEl);
                if (bsChatModal && chatModalEl.classList.contains('show')) {
                    bsChatModal.hide();
                }
            }

            // Show audio call modal
            const audioCallModalEl = document.getElementById('audioCallModal');
            if (audioCallModalEl) {
                const bsAudioCallModal = bootstrap.Modal.getInstance(audioCallModalEl) || new bootstrap.Modal(audioCallModalEl);
                if (!audioCallModalEl.classList.contains('show')) {
                    bsAudioCallModal.show();
                }
            }
            return false; // Indicate that the event was handled
        }
        // If no call is active, let the default action proceed
        console.log("[AudioCallHandler] No call active. Allowing default .chat-modal click behavior.");
        return true;
    }

    _attachContextEventHandlers() {
        const contextList = document.getElementById('audioCallContextDocumentsItems');
        if (contextList) {
            // Use event delegation to handle clicks on remove buttons
            contextList.removeEventListener('click', this._handleRemoveContextDocument); // Prevent multiple listeners
            contextList.addEventListener('click', this._handleRemoveContextDocument);
        }
    }

    _handleRemoveContextDocument(event) {
        if (this.isCallActive || this.isConnecting) {
            // Prevent removal if call is active or connecting
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        const removeButton = event.target.closest('.remove-audiocall-context-doc');
        if (removeButton) {
            const index = parseInt(removeButton.dataset.index, 10);
            if (!isNaN(index) && window.audioCallContextDocuments && index >= 0 && index < window.audioCallContextDocuments.length) {
                const removedDoc = window.audioCallContextDocuments.splice(index, 1);
                console.log(`[AudioCallHandler] Removed context document at index ${index}:`, removedDoc[0]?.title);
                this._updateContextUI(); // Refresh the UI
            } else {
                console.error('[AudioCallHandler] Could not remove context document. Invalid index:', index);
            }
        }
    }

    _updateContextUI() {
        const noContextMessage = document.getElementById('noAudioCallContextMessage');
        const contextDocumentsListContainer = document.getElementById('audioCallContextDocumentsList');
        const contextDocumentsItemsUl = document.getElementById('audioCallContextDocumentsItems');

        if (!noContextMessage || !contextDocumentsListContainer || !contextDocumentsItemsUl) {
            // console.warn("[AudioCallHandler] Context UI elements not found.");
            return;
        }

        const contextLocked = this.isCallActive || this.isConnecting;

        if (!window.audioCallContextDocuments || window.audioCallContextDocuments.length === 0) {
            noContextMessage.style.display = 'block';
            contextDocumentsListContainer.classList.add('d-none');
        } else {
            noContextMessage.style.display = 'none';
            contextDocumentsListContainer.classList.remove('d-none');
            contextDocumentsItemsUl.innerHTML = ''; // Clear current items

            window.audioCallContextDocuments.forEach((doc, index) => {
                const item = document.createElement('li');
                item.className = 'list-group-item d-flex justify-content-between align-items-center bg-transparent border-secondary dynamic-color'; // Added classes for styling
                item.innerHTML = `
                    <span class="text-truncate" title="${doc.title}">${doc.title}</span>
                    <button class="btn btn-sm btn-outline-danger remove-audiocall-context-doc" data-index="${index}" title="Eliminar del contexto" ${contextLocked ? 'disabled style=\'pointer-events:none;opacity:0.5;\'' : ''}>
                        <i class="anticon anticon-delete"></i>
                    </button>
                `;
                contextDocumentsItemsUl.appendChild(item);
            });
        }
    }

    _addActualDocumentToContext(path, title, isUserDoc, isLawDoc) {
        if (!window.audioCallContextDocuments) {
            window.audioCallContextDocuments = [];
        }
        const existingDocIndex = window.audioCallContextDocuments.findIndex(doc => doc.path === path);
        if (existingDocIndex === -1) {
            window.audioCallContextDocuments.push({
                path: path,
                title: title,
                is_user_document: isUserDoc,
                is_law: isLawDoc
            });
            console.log(`[AudioCallHandler] Added "${title}" to audio call context. New context:`, window.audioCallContextDocuments);
        } else {
            console.log(`[AudioCallHandler] Document "${title}" already in audio call context.`);
        }
        this._updateContextUI();
    }

    // Function to start an audio call with the currently viewed document
    startAudioCallWithCurrentDocument() {
        if (typeof isSharedChatDocumentFeatureRestricted === 'function' && isSharedChatDocumentFeatureRestricted()) {
            showSharedChatDocumentFeatureBlockedMessage();
            return;
        }
        // This is a wrapper around existing functionality but exposed as a separate method
        // for consistency with other components
        this.prepareAndShowModal();
    }

    // Function to start an audio call with multiple selected documents
    startAudioCallWithMultiDocument() {
        if (this.isCallActive || this.isConnecting) {
            alert("No se puede modificar el contexto mientras una llamada está activa.");
            return;
        }
        // Close the documents modal
        $(document).trigger('close-user-files-select');

        // Get the checked checkboxes
        let checkboxes = document.querySelectorAll('.document-checkbox:checked');

        if (checkboxes.length === 0) {
            alert("Por favor, seleccione al menos un documento para agregar al contexto de la llamada");
            return;
        }

        // Initialize if needed
        if (!window.audioCallContextDocuments) {
            window.audioCallContextDocuments = [];
        }

        // Process all selected documents
        const paths = [];
        const titles = [];
        const userDocs = [];
        const lawDocs = [];
        let hasNewDocs = false;

        for (let i = 0; i < checkboxes.length; i++) {
            let path = checkboxes[i].getAttribute('data-href');
            let title = checkboxes[i].getAttribute('data-original-filename');
            let isUserDoc = path.includes('/get_user_upload');
            let isLawDoc = false; // Default, could be determined by path pattern if needed

            // Check if this document is already in context
            const existingDocIndex = window.audioCallContextDocuments.findIndex(doc => doc.path === path);
            if (existingDocIndex === -1) {
                paths.push(path);
                titles.push(title);
                userDocs.push(isUserDoc);
                lawDocs.push(isLawDoc);
                hasNewDocs = true;
            } else {
                console.log(`[AudioCallHandler] Multi-select: Document "${title}" already in context, skipping.`);
            }
        }

        // If no *new* documents were selected, just open the modal
        if (!hasNewDocs) {
            console.log("[AudioCallHandler] Multi-select: All selected documents are already in context.");
            this._updateContextUI();
            const audioCallModalEl = document.getElementById('audioCallModal');
            if (audioCallModalEl) {
                const bsModal = bootstrap.Modal.getInstance(audioCallModalEl) || new bootstrap.Modal(audioCallModalEl);
                bsModal.show();
            }
            return;
        }

        // If we have *new* documents and existing context, show confirmation
        if (window.audioCallContextDocuments.length > 0) {
            this._showMultiDocConfirmation(paths, titles, userDocs, lawDocs);
            // Confirmation modal handles opening the main modal
        } else {
            // No existing context, add all *new* selected documents directly
            for (let i = 0; i < paths.length; i++) {
                this._addActualDocumentToContext(paths[i], titles[i], userDocs[i], lawDocs[i]);
            }
            // Open the main modal
            const audioCallModalEl = document.getElementById('audioCallModal');
            if (audioCallModalEl) {
                const bsModal = bootstrap.Modal.getInstance(audioCallModalEl) || new bootstrap.Modal(audioCallModalEl);
                bsModal.show();
            }
        }
    }

    // Helper function to show confirmation when adding multiple documents
    _showMultiDocConfirmation(newPaths, newTitles, newUserDocs, newLawDocs) {
        // Only list the *new* documents to be added
        let messageHtml = `<p>¿Desea agregar los siguientes documentos al contexto actual de la llamada?</p>
            <div class="mt-3">
                <p><strong>Documentos a agregar:</strong></p>
                <ul class="list-group">
                    ${newTitles.map(title => `<li class="list-group-item">${title}</li>`).join('')}
                </ul>
            </div>
            <div class="mt-3">
                <p><strong>Documentos ya en el contexto:</strong></p>
                <ul class="list-group">
                    ${window.audioCallContextDocuments.map(doc => `<li class="list-group-item">${doc.title}</li>`).join('')}
                </ul>
            </div>
        `;

        const modalElement = document.getElementById('addDocumentConfirmModal');
        const messageEl = document.getElementById('addDocumentConfirmMessage');
        const confirmBtn = document.getElementById('confirmAddDocument'); // "Add to context"
        const cancelBtn = document.getElementById('cancelAddDocument');   // "Create new context"
        const keepBtn = document.getElementById('keepContextBtn');       // "Keep current context"

        messageEl.innerHTML = messageHtml;
        confirmBtn.textContent = "Agregar al contexto actual";
        cancelBtn.textContent = "Crear nuevo contexto"; // Explicitly set
        keepBtn.textContent = "Mantener contexto actual";
        keepBtn.style.display = 'inline-block'; // Show the "keep current" button

        const bsModal = new bootstrap.Modal(modalElement);

        // Remove previous listeners
        const newConfirmBtn = confirmBtn.cloneNode(true);
        confirmBtn.parentNode.replaceChild(newConfirmBtn, confirmBtn);
        const newCancelBtn = cancelBtn.cloneNode(true);
        cancelBtn.parentNode.replaceChild(newCancelBtn, cancelBtn);
        const newKeepBtn = keepBtn.cloneNode(true);
        keepBtn.parentNode.replaceChild(newKeepBtn, keepBtn);

        const audioCallHandler = this;

        // Listener for "Agregar al contexto actual"
        newConfirmBtn.addEventListener('click', function handleConfirmMulti() {
            // Add only the *new* documents to the existing context
            for (let i = 0; i < newPaths.length; i++) {
                audioCallHandler._addActualDocumentToContext(newPaths[i], newTitles[i], newUserDocs[i], newLawDocs[i]);
            }
            bsModal.hide();

            // Open main modal
            const audioCallModalEl = document.getElementById('audioCallModal');
            if (audioCallModalEl) {
                const bsAudioModal = bootstrap.Modal.getInstance(audioCallModalEl) || new bootstrap.Modal(audioCallModalEl);
                bsAudioModal.show();
            }
        }, { once: true });

        // Listener for "Crear nuevo contexto"
        newCancelBtn.addEventListener('click', function handleCancelMulti() {
            // Replace context with *only* the new documents
            window.audioCallContextDocuments = [];
            for (let i = 0; i < newPaths.length; i++) {
                audioCallHandler._addActualDocumentToContext(newPaths[i], newTitles[i], newUserDocs[i], newLawDocs[i]);
            }
            bsModal.hide();

            // Open main modal
            const audioCallModalEl = document.getElementById('audioCallModal');
            if (audioCallModalEl) {
                const bsAudioModal = bootstrap.Modal.getInstance(audioCallModalEl) || new bootstrap.Modal(audioCallModalEl);
                bsAudioModal.show();
            }
        }, { once: true });

        // Listener for "Mantener contexto actual"
        newKeepBtn.addEventListener('click', function handleKeepMulti() {
            // Do nothing to the context, just show the modal
            bsModal.hide();

            // Open main modal
            const audioCallModalEl = document.getElementById('audioCallModal');
            if (audioCallModalEl) {
                const bsAudioModal = bootstrap.Modal.getInstance(audioCallModalEl) || new bootstrap.Modal(audioCallModalEl);
                bsAudioModal.show();
            }
        }, { once: true });

        bsModal.show();
    }

    _handleContextConfirmation(action, docDetails) {
        const { path, title, isUserDoc, isLawDoc } = docDetails;
        const audioCallModalEl = document.getElementById('audioCallModal');
        let bsAudioCallModal = bootstrap.Modal.getInstance(audioCallModalEl);
        if (!bsAudioCallModal) {
            bsAudioCallModal = new bootstrap.Modal(audioCallModalEl);
        }

        if (action === 'use_as_context' || action === 'create_new') { // "Usar como contexto" or "Reemplazar contexto"
            window.audioCallContextDocuments = []; // Clear existing
            this._addActualDocumentToContext(path, title, isUserDoc, isLawDoc);
        } else if (action === 'add_to_current') { // "Agregar al contexto actual"
            this._addActualDocumentToContext(path, title, isUserDoc, isLawDoc);
        } else if (action === 'keep_current') { // "Mantener actual (no agregar este doc)" or "Iniciar sin contexto"
            // Do nothing to context if user chose to keep current or start without.
        }

        this._updateContextUI(); // Ensure UI is fresh after any action.

        // Show the main audio call modal if it's not already visible.
        // The confirmation modal hides itself automatically (data-bs-dismiss or manual hide in button handlers).
        if (audioCallModalEl && !audioCallModalEl.classList.contains('show')) {
            bsAudioCallModal.show(); // This will trigger 'shown.bs.modal' for initialize
        }
    }

    _showContextConfirmation(path, title, isUserDoc, isLawDoc) {
        const isContextEmpty = !window.audioCallContextDocuments || window.audioCallContextDocuments.length === 0;
        let messageHtml = '';

        const modalElement = document.getElementById('addDocumentConfirmModal');
        const messageEl = document.getElementById('addDocumentConfirmMessage');
        const confirmBtnEl = document.getElementById('confirmAddDocument'); // Main confirm/positive action
        const cancelBtnEl = document.getElementById('cancelAddDocument');   // Alternative positive/neutral action
        const keepBtnEl = document.getElementById('keepContextBtn');       // Negative/keep action

        if (!modalElement || !messageEl || !confirmBtnEl || !cancelBtnEl || !keepBtnEl) {
            console.error("[AudioCallHandler] Confirmation modal elements not found! Adding document directly.");
            this._addActualDocumentToContext(path, title, isUserDoc, isLawDoc);
            const audioCallModalEl = document.getElementById('audioCallModal');
            if (audioCallModalEl) {
                const bsModal = bootstrap.Modal.getInstance(audioCallModalEl) || new bootstrap.Modal(audioCallModalEl);
                bsModal.show(); // This will trigger 'shown.bs.modal' for initialize
            }
            return;
        }

        messageEl.innerHTML = ''; // Clear previous message

        const confirmBtn = confirmBtnEl.cloneNode(true);
        confirmBtnEl.parentNode.replaceChild(confirmBtn, confirmBtnEl);
        const cancelBtn = cancelBtnEl.cloneNode(true);
        cancelBtnEl.parentNode.replaceChild(cancelBtn, cancelBtnEl);
        const keepBtn = keepBtnEl.cloneNode(true);
        keepBtnEl.parentNode.replaceChild(keepBtn, keepBtnEl);

        const docDetails = { path, title, isUserDoc, isLawDoc };
        const bsConfirmationModal = new bootstrap.Modal(modalElement);

        if (isContextEmpty) {
            messageHtml = `<p>¿Desea usar <strong>${title}</strong> como contexto inicial para la llamada con el Abogado Virtual?</p>`;
            confirmBtn.textContent = "Usar como contexto";
            cancelBtn.textContent = "Iniciar sin contexto";
            keepBtn.style.display = 'none';

            confirmBtn.onclick = () => { bsConfirmationModal.hide(); this._handleContextConfirmation('use_as_context', docDetails); };
            cancelBtn.onclick = () => { bsConfirmationModal.hide(); this._handleContextConfirmation('keep_current', docDetails); }; // keep_current effectively means no context added
        } else {
            messageHtml = `<p>Tiene documentos en el contexto actual de la llamada. ¿Cómo desea proceder con <strong>${title}</strong>?</p>`;
            messageHtml += `
                <div class="mt-3">
                    <p><strong>Documentos en contexto actual:</strong></p>
                    <ul class="list-group mb-0" style="max-height: 150px; overflow-y: auto;">
                        ${window.audioCallContextDocuments.map(doc => `<li class="list-group-item dynamic-color bg-transparent border-secondary">${doc.title}</li>`).join('')}
                    </ul>
                </div>
            `;
            confirmBtn.textContent = "Agregar al actual";
            cancelBtn.textContent = "Reemplazar contexto actual"; // (i.e. use only this new doc)
            keepBtn.textContent = "Mantener actual (no agregar)";
            keepBtn.style.display = 'inline-block';

            confirmBtn.onclick = () => { bsConfirmationModal.hide(); this._handleContextConfirmation('add_to_current', docDetails); };
            cancelBtn.onclick = () => { bsConfirmationModal.hide(); this._handleContextConfirmation('create_new', docDetails); };
            keepBtn.onclick = () => { bsConfirmationModal.hide(); this._handleContextConfirmation('keep_current', docDetails); };
        }

        messageEl.innerHTML = messageHtml;
        bsConfirmationModal.show();
    }

    // Called by prepareAndShowModal
    addCurrentDocumentToContext() {
        let path = null;
        let page_title = window.page_title;
        let is_user_document = window.is_user_document || false;
        let is_law = window.is_law || false;

        if (window.is_document || is_user_document || is_law) {
            path = window.current_document_path;
        }

        if (path && page_title) {
            page_title = page_title.replace(/\.html$/i, "");
        }

        if (!path && window.current_summary_path) {
            path = decodeURIComponent(window.current_summary_path);
            // Fetch title for summary path if not available
            if (!page_title) {
                $.ajax({
                    url: "/get_page_title",
                    type: "GET",
                    data: { "path": path },
                    async: false, // Synchronous for simplicity
                    success: function (response) {
                        page_title = response.page_title;
                        // Infer flags based on path
                        is_user_document = path.includes('/get_user_upload') || window.is_user_document;
                        is_law = window.is_law;
                    },
                    error: function () {
                        console.error("[AudioCallHandler] Failed to get page title for summary path:", path);
                        page_title = "Resumen Actual"; // Fallback title
                    }
                });
            }
        } else if (path) {
            path = decodeURIComponent(path);
        }

        if (!path || !page_title) {
            console.log("[AudioCallHandler] Context: No valid document path/title found to add.");
            return false; // No document processed for confirmation
        }

        if (is_user_document && path && !path.startsWith('/get_user_upload') && !path.includes('filename=')) {
            path = buildUserUploadHref(path, page_title, window.current_user_document_scope);
        }

        const existingDocIndex = window.audioCallContextDocuments.findIndex(doc => doc.path === path);
        if (existingDocIndex !== -1) {
            console.log(`[AudioCallHandler] Context: Document "${page_title}" is already in context. Modal will show directly.`);
            this._updateContextUI(); // Ensure UI is up-to-date.
            // The main modal will be shown by prepareAndShowModal's fallback logic
            return false; // false indicates no *new* doc was processed for confirmation, but current context is fine.
        }

        this._showContextConfirmation(path, page_title, is_user_document, is_law);
        return true; // Confirmation process started for a new document
    }

    // Called by App.js when user clicks the button to start an audio call
    prepareAndShowModal() {
        if (this.isCallActive || this.isConnecting) {
            alert("No se puede modificar el contexto mientras una llamada está activa.");
            return;
        }
        // Hide the main chat modal first
        const chatModal = document.getElementById('chatModal');
        if (chatModal) {
            const bootstrapChatModal = bootstrap.Modal.getInstance(chatModal);
            if (bootstrapChatModal && chatModal.classList.contains('show')) {
                bootstrapChatModal.hide();
            }
        }

        const confirmationStarted = this.addCurrentDocumentToContext();

        if (!confirmationStarted) {
            // No new document was sent to confirmation (either no current doc, or it was a duplicate).
            // Directly show the audio call modal.
            this._updateContextUI(); // Ensure context UI is up-to-date.
            const audioCallModalEl = document.getElementById('audioCallModal');
            if (audioCallModalEl) {
                const bsAudioCallModal = bootstrap.Modal.getInstance(audioCallModalEl) || new bootstrap.Modal(audioCallModalEl);
                if (!audioCallModalEl.classList.contains('show')) {
                    bsAudioCallModal.show(); // This will trigger 'shown.bs.modal' for initialize via App.js
                }
            }
        }
        // If confirmationStarted is true, _handleContextConfirmation is responsible for showing the audioCallModal.
    }

    _attachEventHandlers() {
        const startCallBtn = document.getElementById('start-call-btn');
        const stopCallBtn = document.getElementById('stop-call-btn');
        const closeModalBtn = document.getElementById('close-audio-modal-btn');

        if (startCallBtn) {
            startCallBtn.addEventListener('click', () => this.startCall());
        }

        if (stopCallBtn) {
            stopCallBtn.addEventListener('click', () => this.stopCall());
        }

        if (closeModalBtn) {
            closeModalBtn.addEventListener('click', () => this.closeModal());
        }

        // Handle modal close event
        const audioCallModal = document.getElementById('audioCallModal');
        if (audioCallModal) {
            if (this.callVoiceDefaultModal !== audioCallModal) {
                audioCallModal.addEventListener('show.bs.modal', () => this._resetCallVoiceDefault());
                this.callVoiceDefaultModal = audioCallModal;
            }
            audioCallModal.addEventListener('hidden.bs.modal', () => {
                // if (this.isCallActive || this.isConnecting) {
                //     this.stopCall();
                // }
                // Call continues in background if modal is hidden.
                // Call is only stopped by explicit user action (stop button)
                // or by server-side events (e.g., idle termination, errors).
                console.log("[AudioCallHandler] audioCallModal hidden. Call continues if active.");

                // Add visual cue to the main chat button if call is active
                const chatModalButton = document.querySelector('.chat-modal');
                if (chatModalButton && (this.isCallActive || this.isConnecting)) {
                    chatModalButton.classList.add('audio-call-active-indicator');
                }
            });
        }
    }

    _log(message) {
        console.log(`[AudioCallHandler] ${message}`);

        // Add visual cue to the main chat button if call is active
        const chatModalButton = document.querySelector('.chat-modal');
        if (chatModalButton && (this.isCallActive || this.isConnecting)) {
            chatModalButton.classList.add('audio-call-active-indicator');
        }
    }

    _updateUI() {
        const callStatusEl = document.getElementById('audio-call-status');
        const elapsedTimeEl = document.getElementById('audio-call-elapsed-time');
        const errorMessageEl = document.getElementById('audio-call-error');
        const startCallBtn = document.getElementById('start-call-btn');
        const stopCallBtn = document.getElementById('stop-call-btn');
        const introTextEl = document.getElementById('audio-call-intro-text');

        if (callStatusEl) {
            if (this.isCallActive) {
                callStatusEl.innerHTML = `
                    <div class="text-green-500 font-semibold flex items-center justify-center text-lg">
                        <i class="anticon anticon-sound mr-2 animate-pulse text-xl"></i>
                        Llamada en curso...
                    </div>`;
            } else if (this.isConnecting) {
                callStatusEl.innerHTML = `
                    <div class="text-yellow-500 font-semibold flex items-center justify-center text-lg">
                        <div class="spinner-border spinner-border-sm text-yellow-500 mr-2" role="status"></div>
                        Conectando...
                    </div>`;
            } else {
                callStatusEl.innerHTML = '';
            }
        }

        if (introTextEl) {
            if (this.isCallActive || this.isConnecting) {
                introTextEl.style.display = 'none';
            } else {
                introTextEl.style.display = 'block';
            }
        }

        if (elapsedTimeEl) {
            const totalSeconds = Math.floor(this.elapsedTime / 1000);
            const minutes = Math.floor(totalSeconds / 60).toString().padStart(2, '0');
            const seconds = (totalSeconds % 60).toString().padStart(2, '0');
            elapsedTimeEl.textContent = `${minutes}:${seconds}`;
        }

        if (errorMessageEl) {
            errorMessageEl.style.display = this.helper.errorMessage ? 'block' : 'none';
            if (this.helper.errorMessage) {
                errorMessageEl.textContent = this.helper.errorMessage;
            }
        }

        if (startCallBtn) {
            startCallBtn.disabled = this.isCallActive || this.isConnecting;
            startCallBtn.classList.toggle('opacity-50', this.isCallActive || this.isConnecting);

            if (this.isConnecting && !this.isCallActive) {
                startCallBtn.innerHTML = `<div class="spinner-border text-white" style="width: 2rem; height: 2rem;" role="status"></div>`;
            } else {
                startCallBtn.innerHTML = `<i class="anticon anticon-phone text-3xl"></i>`;
            }
            // Also disable gender toggle when call is active or connecting
            document.querySelectorAll('input[name="gender"]').forEach(input => {
                input.disabled = this.isCallActive || this.isConnecting;
            });
        }

        if (stopCallBtn) {
            const icon = stopCallBtn.querySelector('i.anticon');
            stopCallBtn.disabled = !this.isCallActive && !this.isConnecting;

            if (this.isCallActive || this.isConnecting) {
                stopCallBtn.classList.add('stop-button-active'); // CSS handles active bg/icon color
                stopCallBtn.classList.remove('bg-gray-400', 'text-gray-700');
                if (icon) {
                    icon.classList.remove('text-gray-700');
                    // icon.classList.add('text-white'); // Handled by injected CSS
                }
            } else {
                stopCallBtn.classList.remove('stop-button-active');
                stopCallBtn.classList.add('bg-gray-400', 'text-gray-700');
                if (icon) {
                    icon.classList.add('text-gray-700');
                    icon.classList.remove('text-white');
                }
            }
        }
        // Call context UI update to reflect lock state
        this._updateContextUI();
    }

    async closeModal() {
        // if (this.isCallActive || this.isConnecting) {
        //     // If closeModal is not async, return the promise so caller can await if needed
        //     return this.handleCallTermination(true);
        // }
        // Call continues in background if modal is closed.
        // User must explicitly click "stop call" button to terminate.

        const audioCallModal = document.getElementById('audioCallModal');
        if (audioCallModal) {
            const bsModal = bootstrap.Modal.getInstance(audioCallModal);
            if (bsModal) {
                bsModal.hide();
            }
        }

        // Optionally show the chat modal again
        const chatModal = document.getElementById('chatModal');
        if (chatModal) {
            const bootstrapModal = bootstrap.Modal.getInstance(chatModal) || new bootstrap.Modal(chatModal);
            // Uncomment if you want the chat modal to reappear when audio call closes
            // bootstrapModal.show();
        }
    }

    async startCall(demoSession = null) {
        if (this.isCallActive || this.isConnecting) return;
        const isDemoCall = typeof demoSession?.token === 'string' && !!demoSession.token;

        // Check if user is logged in
        if (typeof window.user_email === 'undefined' || !window.user_email) {
            this._log("User not logged in. Aborting call start.");
            this.helper.errorMessage = "Debe iniciar sesión o registrarse para realizar una llamada.";
            this.handleCallTermination(false); // Reset UI and state
            return;
        }

        this._log("Starting call...");
        if (window.LexiusResponseAudio) window.LexiusResponseAudio.pause();
        this.isConnecting = true;
        this._setMicrophoneOwnership(!isDemoCall);
        const callAttemptId = ++this.callAttemptId;
        const isCurrentAttempt = () => (
            this.callAttemptId === callAttemptId && this.isConnecting
        );
        this.helper.errorMessage = null;
        this.helper.clearMessages();
        this.helper.isSearching = false;
        this.helper.searchQuery = '';
        this.helper.searchResults = [];
        this.currentRoomUrl = null; // Reset room URL at the start of a new call

        // Capture the current context for this call (deep copy)
        this.currentCallContext = (window.audioCallContextDocuments || []).map(doc => ({ ...doc }));

        // Clear any previous idle notices when a new call starts
        const idleNoticeEl = document.getElementById('audio-call-idle-notice');
        if (idleNoticeEl) {
            idleNoticeEl.style.display = 'none';
            idleNoticeEl.innerHTML = '';
        }

        // Get context document paths
        const contextDocumentPaths = (window.audioCallContextDocuments || []).map(doc => doc.path);
        console.log("[AudioCallHandler] Starting call with context paths:", contextDocumentPaths);

        this._updateUI(); // General UI update
        // this._updateContextUI(); // Context UI should be up-to-date from prepareAndShowModal or initialize

        try {
            // Resolve voice and derive gender from the selected voice to avoid confusion with separate toggles
            const selectedVoiceId = (function () {
                const select = document.getElementById('callVoiceSelect');
                if (!select) return null;
                const val = select.value;
                return val && val.trim() !== '' ? val : null;
            })();

            let selectedGender = 'male';
            const vForGender = this._getCallVoiceById(selectedVoiceId);
            if (vForGender && vForGender.gender) { selectedGender = vForGender.gender; }
            this._log(`Derived gender from selected voice: ${selectedGender}`);

            // Check for first call cookie
            const cookieName = "lexius_voice_call_used";
            let isFirstCall = false;
            if (!getaCookie(cookieName)) {
                this._log("First call detected. Setting cookie.");
                setaCookie(cookieName, "true", 365); // Set cookie for 1 year
                isFirstCall = true;
            } else {
                this._log("Not the first call (cookie found).");
            }

            if (window.user_is_free) {
                this._log("User is free user. Not calling voice API.");
                this.helper.errorMessage = "Debes adquirir un plan Premium para poder realizar llamadas.";
                this.handleCallTermination(false);
                return;
            }

            // 1. Authenticate with Flask server to get voice_access_token
            this._log(`Authenticating with Flask server at ${this.flaskServerUrl}/voice-api-authenticate`);
            let voiceAccessToken = null;
            try {
                const authResponse = await fetch(`${this.flaskServerUrl}/voice-api-authenticate`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        // Cookies should be sent automatically by the browser if on the same domain
                    },
                    // body: JSON.stringify({}), // No body needed for this endpoint currently
                });

                if (!isCurrentAttempt()) return;

                if (!authResponse.ok) {
                    // Handle 401 Unauthorized status specifically
                    if (authResponse.status === 401) {
                        this._log("[Error] Authentication failed: Session expired (401)");
                        this.handleCallTermination(false);
                        handleSessionExpired("llamadas de voz");
                        return;
                    }

                    // Try to parse JSON, but handle cases where response is not JSON
                    let errorMessage = `Error de autenticación: ${authResponse.status}`;
                    try {
                        const errorData = await authResponse.json();
                        errorMessage = errorData.message || errorMessage;
                    } catch (jsonError) {
                        if (!isCurrentAttempt()) return;
                        // Response is not JSON (might be HTML error page), use default message
                        this._log(`[Warning] Non-JSON error response: ${jsonError.message}`);
                    }

                    if (!isCurrentAttempt()) return;
                    this._log(`[Error] Voice API Authentication failed: ${errorMessage}`);
                    this.helper.errorMessage = errorMessage;
                    this.handleCallTermination(false);
                    return;
                }
                const authData = await authResponse.json();
                if (!isCurrentAttempt()) return;
                voiceAccessToken = authData.access_token;
                if (!voiceAccessToken) {
                    this._log("[Error] Voice access token not received from Flask server.");
                    this.helper.errorMessage = "No se pudo obtener el token de acceso para la API de voz.";
                    this.handleCallTermination(false);
                    return;
                }
                this._log("Successfully obtained voice access token.");
            } catch (authErr) {
                if (!isCurrentAttempt()) return;
                console.error("Error during voice API authentication:", authErr);
                this.helper.errorMessage = authErr.message || "Error de red durante la autenticación de la API de voz.";
                this.handleCallTermination(false);
                return;
            }

            const requestData = {
                country: window.country,
                gender: selectedGender,
                is_first_call: isFirstCall,
                voice_access_token: voiceAccessToken,
                context: contextDocumentPaths,
                user_name: window.user_id,
                voice_id: selectedVoiceId,
                ...(isDemoCall ? { demo_token: demoSession.token } : {}),
            };

            const dailyTransport = this._createDailyTransport({
                dailyConfig: {
                    micAudioMode: 'speech',
                    // Preserve the pre-1.4 transport behavior for device selection.
                    useDevicePreferenceCookies: false,
                },
                inputSettings: {
                    audio: {
                        settings: {
                            echoCancellation: true,
                            noiseSuppression: true,
                            autoGainControl: true,
                        },
                    },
                },
            });

            const pipecatClient = this._createPipecatClient({
                transport: dailyTransport,
                enableMic: !isDemoCall,
                enableCam: false, // No camera for Lexius
                disconnectOnBotDisconnect: true,
            });
            this.rtviClient = pipecatClient;
            const ownsCurrentClient = () => (
                this.callAttemptId === callAttemptId
                && this.rtviClient === pipecatClient
            );
            const disconnectStaleClient = async () => {
                await this._disconnectPipecatClientBestEffort(pipecatClient);
            };

            // Pipecat 1.x no longer supports custom message helpers and ignores
            // unknown RTVI message types. Keep the existing backend contract by
            // routing Lexius app messages through Daily's public call client.
            const dailyCallClient = dailyTransport.dailyCallClient;
            this.dailyCallClient = dailyCallClient;
            dailyCallClient.on('app-message', this._handleDailyAppMessage);

            // Register event handlers
            pipecatClient.on(RTVIEvent.Connected, () => {
                if (!ownsCurrentClient()) {
                    disconnectStaleClient();
                    return;
                }
                this._log("PipecatClient connected to Daily room.");
            });

            pipecatClient.on(RTVIEvent.Disconnected, () => {
                if (!ownsCurrentClient()) return;
                this._log("PipecatClient disconnected.");
                this.handleCallTermination(true); // True for clean disconnect
            });

            pipecatClient.on(RTVIEvent.TransportStateChanged, (state) => {
                if (!ownsCurrentClient()) return;
                this._log(`Transport state: ${state}`);
            });

            pipecatClient.on(RTVIEvent.BotStarted, (connectionParams) => {
                if (!ownsCurrentClient()) return;
                const roomUrl = connectionParams?.room_url
                    || connectionParams?.dailyRoom
                    || connectionParams?.url;

                if (typeof roomUrl === 'string' && roomUrl) {
                    this.currentRoomUrl = roomUrl;
                    this._log(`Daily room URL obtained from /connect: ${this.currentRoomUrl}`);
                } else {
                    this._log('[Warning] /connect did not return a Daily room URL.');
                }
            });

            pipecatClient.on(RTVIEvent.BotReady, (data) => {
                if (!ownsCurrentClient()) return;
                this._log(`Bot ready: ${JSON.stringify(data)}`);
                if (!this.isConnecting) { // Check if stopCall was called during connection
                    this._log("Call was stopped before bot became ready. Not activating.");
                    return;
                }
                this.isConnecting = false;
                this.isCallActive = true;
                this.startTimer();
                this._updateUI();
            });

            // We are not using botTranscript events

            // this.rtviClient.on(RTVIEvent.BotTranscript, (data) => {
            //     this._log(`Bot transcript: ${data.text}`);
            //     const messagesContainer = document.getElementById('audio-call-messages');
            //     if (messagesContainer) {
            //         const messages = messagesContainer.querySelectorAll('div');
            //         const lastMsg = messages.length > 0 ? messages[messages.length - 1].textContent : '';

            //         if (lastMsg && lastMsg.startsWith("Virtual: [Hablando")) {
            //             messages[messages.length - 1].remove();
            //         }

            //         if (data.text && data.text.trim() !== "") {
            //             this.helper.addMessage(`Abogado Virtual: ${data.text}`);
            //         }
            //     }
            // });

            pipecatClient.on(RTVIEvent.Error, (error) => {
                if (!ownsCurrentClient()) return;
                if (error?.data?.fatal === false) {
                    console.warn("Recoverable Pipecat server error:", error);
                    return;
                }
                console.error("PipecatClient error:", error);
                this.helper.errorMessage = error?.data?.message
                    || error?.data?.error
                    || error?.message
                    || "Ocurrió un error en la llamada.";
                if (!error?.data?.fatal) {
                    this.handleCallTermination(false);
                }
            });

            pipecatClient.on(RTVIEvent.TrackStarted, (track, participant) => {
                if (!ownsCurrentClient()) return;
                this._log(`Track started: ${track.kind} from ${participant?.local ? 'local' : 'remote participant'}`);
                if (!participant?.local && track.kind === 'audio' && this.botAudioElement) {
                    this._log("Assigning bot audio track to HTML audio element.");
                    this.botAudioElement.srcObject = new MediaStream([track]);
                    this.botAudioElement.play().catch(e => console.warn("Autoplay failed for bot audio:", e));
                }
            });

            pipecatClient.on(RTVIEvent.TrackStopped, (track, participant) => {
                if (!ownsCurrentClient()) return;
                this._log(`Track stopped: ${track.kind} from ${participant?.local ? 'local' : 'remote participant'} (ID: ${track.id})`);
                if (!participant?.local && track.kind === 'audio' && this.botAudioElement) {
                    const mediaStream = this.botAudioElement.srcObject;
                    // Check if the stopped track is part of the bot's audio stream
                    if (mediaStream && mediaStream.getAudioTracks().find(t => t.id === track.id)) {
                        this._log("Bot audio track (remote) stopped. Clearing srcObject.");
                        this.botAudioElement.srcObject = null; // Clear it as before

                        // If the call is still marked as active or connecting on the frontend,
                        // initiate termination. The handleCallTermination method has its own
                        // guard to prevent multiple executions.
                        if (this.isCallActive || this.isConnecting) {
                            this._log("Triggering call termination due to bot audio track stopping.");
                            this.handleCallTermination(false); // Treat as a clean disconnect for UI reset
                        }
                    }
                }
            });

            this._log("Initializing devices (requesting microphone)...");
            try {
                if (!isDemoCall) await pipecatClient.initDevices(); // Normal calls request microphone permission

                if (!ownsCurrentClient() || !this.isConnecting) {
                    await disconnectStaleClient();
                    return;
                }

                const micState = pipecatClient.mediaState?.mic;
                if (!isDemoCall && micState?.state !== 'granted') {
                    this._log(`[AudioCallHandler] Microphone check failed. State: ${micState?.state || 'unknown'}`);
                    this.helper.errorMessage = "No se pudo acceder al micrófono. Por favor, verifique que tenga un micrófono conectado y que haya otorgado permisos de audio a este sitio web en la configuración de su navegador.";
                    this.handleCallTermination(false);
                    return;
                }
                this._log("[AudioCallHandler] Microphone check successful.");

            } catch (deviceErr) {
                if (!ownsCurrentClient()) {
                    await disconnectStaleClient();
                    return;
                }
                console.error("[AudioCallHandler] PipecatClient.initDevices() promise rejected:", deviceErr);
                const micReason = pipecatClient.mediaState?.mic?.reason;
                if (
                    deviceErr.name === 'NotAllowedError'
                    || deviceErr.name === 'PermissionDeniedError'
                    || micReason === 'blocked'
                ) {
                    this.helper.errorMessage = "Permiso de micrófono denegado. Por favor, verifique la configuración de su navegador.";
                } else if (
                    deviceErr.name === 'NotFoundError'
                    || micReason === 'not-found'
                    || (deviceErr.message && deviceErr.message.toLowerCase().includes('no microphone'))
                ) {
                    this.helper.errorMessage = "No se encontró un micrófono en su dispositivo. Por favor, conecte un micrófono e inténtelo de nuevo.";
                } else {
                    this.helper.errorMessage = "Error al inicializar dispositivos de audio: " + deviceErr.message;
                }
                this.handleCallTermination(false);
                return; // Exit startCall
            }

            this._log("Starting Pipecat bot via /connect...");
            const connectionParams = await pipecatClient.startBot({
                endpoint: `${this.lexiusServerUrl}/connect`,
                requestData,
            });

            if (!ownsCurrentClient() || !this.isConnecting) {
                const canceledRoomUrl = connectionParams?.room_url
                    || connectionParams?.dailyRoom
                    || connectionParams?.url;
                await disconnectStaleClient();
                await this._deleteRoomBestEffort(canceledRoomUrl);
                return;
            }

            this._log("Joining the Daily room...");
            pipecatClient.connect(connectionParams).catch(error => {
                if (!ownsCurrentClient()) return;
                console.error("Error joining the Daily room:", error);
                this.helper.errorMessage = error?.message || "Fallo al conectar la llamada.";
                this.handleCallTermination(false);
            });

            // onBotReady will set isCallActive and start the timer

        } catch (err) {
            if (this.callAttemptId !== callAttemptId) return;
            console.error("Error during startCall:", err);
            this.helper.errorMessage = err.message || "Fallo al iniciar la llamada.";
            if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                this.helper.errorMessage = "Permiso de micrófono denegado.";
            }
            this.handleCallTermination(false);
        }
    }

    async stopCall() {
        this._log("User initiated stopCall.");

        // Clear idle notice because this is a user-initiated stop
        const idleNoticeEl = document.getElementById('audio-call-idle-notice');
        if (idleNoticeEl) {
            idleNoticeEl.style.display = 'none';
            idleNoticeEl.innerHTML = '';
            this._log("Idle notice cleared due to user-initiated stop.");
        }

        // If stopCall is not async, return the promise so caller can await if needed
        return this.handleCallTermination(true); // Treat user stop as a clean termination for state reset
    }

    /**
     * Fetch call transcript from the voice API with small retries.
     * This mitigates race conditions where the bot has not finished
     * writing the transcript file yet when the call ends.
     */
    async _fetchTranscriptWithRetries(roomUrlToFetch, contextForTranscript, maxAttempts = 3, initialDelayMs = 400) {
        if (!roomUrlToFetch) {
            this._log("No room URL provided to _fetchTranscriptWithRetries.");
            return;
        }

        for (let attempt = 1; attempt <= maxAttempts; attempt++) {
            try {
                this._log(`Attempting to fetch transcript (attempt ${attempt}) for room: ${roomUrlToFetch}`);
                const response = await fetch(`${this.lexiusServerUrl}/get_transcript`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ room_url: roomUrlToFetch }),
                });

                if (response.ok) {
                    const transcriptData = await response.json();
                    this._log("Successfully fetched transcript from server.");
                    // Pass the captured context to the transcript handler
                    await this.helper._handleCallTranscript(transcriptData, contextForTranscript);
                    return;
                }

                const errorData = await response.text();
                this._log(`Error fetching transcript (attempt ${attempt}): ${response.status} - ${errorData}`);
            } catch (fetchError) {
                this._log(`Network error fetching transcript (attempt ${attempt}): ${fetchError.message}`);
            }

            if (attempt < maxAttempts) {
                const delay = initialDelayMs * attempt;
                this._log(`Waiting ${delay}ms before retrying transcript fetch...`);
                await new Promise(resolve => setTimeout(resolve, delay));
            }
        }

        this._log("Giving up on fetching transcript after multiple attempts.");
    }

    async handleCallTermination(isCleanDisconnect) {
        if (this.processingTermination) {
            this._log("Termination already in progress. Skipping duplicate handling.");
            return;
        }
        this.processingTermination = true;
        this.callAttemptId += 1;
        this._log(`Handling call termination. Clean disconnect: ${isCleanDisconnect}`);
        let transcriptRequest = null;

        try {
            if (this.timerInterval) {
                clearInterval(this.timerInterval);
                this.timerInterval = null;
            }

            const pipecatClient = this.rtviClient;
            const dailyCallClient = this.dailyCallClient;
            const roomUrlToFetch = this.currentRoomUrl;
            const contextForTranscript = this.currentCallContext;
            const canceledBeforeReady = this.isConnecting && !this.isCallActive;

            // Stop accepting backend UI messages before leave() can trigger any
            // final Daily app-message events for this call.
            if (dailyCallClient && typeof dailyCallClient.off === 'function') {
                dailyCallClient.off('app-message', this._handleDailyAppMessage);
            }
            this.dailyCallClient = null;

            if (pipecatClient) {
                this._log("Disconnecting PipecatClient...");
                await this._disconnectPipecatClientBestEffort(pipecatClient);
                this._log("PipecatClient disconnect cleanup completed.");
            }

            this.rtviClient = null;

            if (canceledBeforeReady && roomUrlToFetch) {
                await this._deleteRoomBestEffort(roomUrlToFetch);
            } else if (roomUrlToFetch) {
                transcriptRequest = { roomUrl: roomUrlToFetch, context: contextForTranscript };
            } else if (pipecatClient) {
                this._log("No room URL available to fetch transcript.");
            }

            if (this.botAudioElement && this.botAudioElement.srcObject) {
                const mediaStream = this.botAudioElement.srcObject;
                if (mediaStream && typeof mediaStream.getTracks === 'function') {
                    mediaStream.getTracks().forEach(track => track.stop());
                }
                this.botAudioElement.srcObject = null;
                this.botAudioElement.pause();
                this.botAudioElement.load(); // Reset audio element
                this._log("Bot audio element stream stopped and cleared.");
            }

            if (!isCleanDisconnect && !this.helper.errorMessage) {
                this.helper.errorMessage = "Llamada terminada inesperadamente.";
            }

            // Reset state variables
            this.isCallActive = false;
            this.isConnecting = false; // Ensure this is reset
            this._resetCallVoiceDefault();
            this.elapsedTime = 0;
            this.callStartTime = null;
            this.currentRoomUrl = null; // Explicitly reset here after all operations if not already

            // Re-enable gender toggle
            document.querySelectorAll('input[name="gender"]').forEach(input => {
                input.disabled = false;
            });

            // Clear search results and query from helper state
            this.helper.isSearching = false;
            this.helper.searchQuery = '';
            this.helper.searchResults = [];
            this.helper.searchHistory = [];
            this.helper.historyIndex = -1;
            // this.helper.currentDisplayedResults = null; // Not needed

            // Clear document history from helper state
            this.helper.documentHistory = [];
            this.helper.documentHistoryIndex = -1;

            // Explicitly update search status UI to ensure "Buscando..." is hidden
            if (this.helper && typeof this.helper._updateSearchStatus === 'function') {
                this.helper._updateSearchStatus(false, ''); // Hides search status, shows call status (which will be cleared by _updateUI)
            }

            // Remove visual cue from main chat button
            const chatModalButton = document.querySelector('.chat-modal');
            if (chatModalButton) {
                chatModalButton.classList.remove('audio-call-active-indicator');
            }

            // Do NOT clear idle notices here if the call was terminated due to idle
            // It will be cleared when a new call starts.
            // The logic for clearing upon user-initiated stop is now in stopCall().
            /*
            const idleNoticeEl = document.getElementById('audio-call-idle-notice');
            if (idleNoticeEl) {
                idleNoticeEl.style.display = 'none';
                idleNoticeEl.innerHTML = '';
            }
            */

            this._log(`Call terminated. isCallActive: ${this.isCallActive}, isConnecting: ${this.isConnecting}`);

            // Clear DataTables if they exist & clear table bodies
            if ($.fn.dataTable && $.fn.dataTable.isDataTable) {
                const jurisprudenceTableJQuery = $('#jurisprudenceResultsTable');
                if (jurisprudenceTableJQuery.length > 0 && $.fn.dataTable.isDataTable(jurisprudenceTableJQuery)) {
                    console.log("[AudioCallHandler] Destroying Jurisprudence DataTable on call termination.");
                    jurisprudenceTableJQuery.DataTable().destroy();
                }
                const jurisprudenceTableBody = document.getElementById('jurisprudenceResultsTableBody');
                if (jurisprudenceTableBody) {
                    console.log("[AudioCallHandler] Clearing Jurisprudence table body on call termination.");
                    jurisprudenceTableBody.innerHTML = '';
                }

                const legislationTableJQuery = $('#legislationResultsTable');
                if (legislationTableJQuery.length > 0 && $.fn.dataTable.isDataTable(legislationTableJQuery)) {
                    console.log("[AudioCallHandler] Destroying Legislation DataTable on call termination.");
                    legislationTableJQuery.DataTable().destroy();
                }
                const legislationTableBody = document.getElementById('legislationResultsTableBody');
                if (legislationTableBody) {
                    console.log("[AudioCallHandler] Clearing Legislation table body on call termination.");
                    legislationTableBody.innerHTML = '';
                }

                const userDocumentTableJQuery = $('#userDocumentResultsTable');
                if (userDocumentTableJQuery.length > 0 && $.fn.dataTable.isDataTable(userDocumentTableJQuery)) {
                    console.log("[AudioCallHandler] Destroying User Document DataTable on call termination.");
                    userDocumentTableJQuery.DataTable().destroy();
                }
                const userDocumentTableBody = document.getElementById('userDocumentResultsTableBody');
                if (userDocumentTableBody) {
                    console.log("[AudioCallHandler] Clearing User Document table body on call termination.");
                    userDocumentTableBody.innerHTML = '';
                }
            }

            // Hide specific search results containers
            const jurisprudenceResultsContainer = document.getElementById('jurisprudence-results-container');
            if (jurisprudenceResultsContainer) {
                jurisprudenceResultsContainer.style.display = 'none';
            }
            const legislationResultsContainer = document.getElementById('legislation-results-container');
            if (legislationResultsContainer) {
                legislationResultsContainer.style.display = 'none';
            }
            const userDocumentResultsContainer = document.getElementById('user-document-results-container');
            if (userDocumentResultsContainer) {
                userDocumentResultsContainer.style.display = 'none';
            }

            // Clear the document content panel
            const documentContentContainer = document.getElementById('document-content-container');
            if (documentContentContainer) {
                documentContentContainer.innerHTML = '';
            }

            // Hide search history navigation
            const searchHistoryNav = document.getElementById('search-history-nav');
            if (searchHistoryNav) {
                searchHistoryNav.style.display = 'none';
            }
            // Hide document history navigation
            const documentHistoryNav = document.getElementById('document-history-nav');
            if (documentHistoryNav) {
                documentHistoryNav.style.display = 'none';
            }
        } finally {
            this.isCallActive = false;
            this.isConnecting = false;
            this._setMicrophoneOwnership(false);
            this.processingTermination = false; // Reset the flag
            this._updateUI(); // Update UI to reflect terminated call state
            // Keep slow persistence independent of microphone ownership and the
            // next call. Use this call's captured room and document context.
            if (transcriptRequest) {
                await this._fetchTranscriptWithRetries(transcriptRequest.roomUrl, transcriptRequest.context);
            }
        }
    }

    startTimer() {
        if (this.timerInterval) clearInterval(this.timerInterval);
        this.callStartTime = Date.now();
        this.elapsedTime = 0;
        this.timerInterval = setInterval(() => {
            this.elapsedTime = Date.now() - this.callStartTime;
            this._updateUI();
        }, 1000);
    }

    _showTopScreenAlert(message, type = 'info', showIcon = false) {
        const alertContainer = document.getElementById('top-screen-alert-container');
        if (!alertContainer) {
            console.warn('[AudioCallHandler] Top screen alert container not found.');
            return;
        }

        // Clear any existing alert timeout
        if (this.topAlertTimeout) {
            clearTimeout(this.topAlertTimeout);
        }

        // Create alert element
        const alertId = `top-alert-${Date.now()}`;
        const alertDiv = document.createElement('div');
        alertDiv.id = alertId;
        alertDiv.className = `bootstrap-alert alert alert-${type} alert-dismissible fade show`;
        alertDiv.setAttribute('role', 'alert');

        let iconHtml = '';
        if (showIcon) {
            iconHtml = '<i class="anticon anticon-phone me-2"></i>';
        }

        alertDiv.innerHTML = `
            ${iconHtml}
            ${message}
            <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
        `;

        // Remove any existing alert
        while (alertContainer.firstChild) {
            alertContainer.removeChild(alertContainer.firstChild);
        }

        alertContainer.appendChild(alertDiv);

        // Automatically dismiss after 5 seconds
        this.topAlertTimeout = setTimeout(() => {
            const currentAlert = document.getElementById(alertId);
            if (currentAlert) {
                const bsAlert = bootstrap.Alert.getInstance(currentAlert) || new bootstrap.Alert(currentAlert);
                if (bsAlert) {
                    bsAlert.close();
                }
            }
        }, 5000);
    }
}

// Create global instance
window.audioCallHandler = new AudioCallHandler();

// Add necessary CSS
const style = document.createElement('style');
style.textContent = `
@keyframes spinner-border {
  to { transform: rotate(360deg); }
}
@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}
@keyframes stop-button-pulse {
  0% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.7); }
  70% { box-shadow: 0 0 0 10px rgba(220, 38, 38, 0); }
  100% { box-shadow: 0 0 0 0 rgba(220, 38, 38, 0); }
}
.animate-pulse { animation: pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite; }

/* Styles for the ACTIVE stop button and its icon */
#stop-call-btn.stop-button-active {
  background-color: #DC2626 !important; /* Tailwind red-600 */
  animation: stop-button-pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}
#stop-call-btn.stop-button-active i.anticon-disconnect {
  color: white !important;
}

.spinner-border {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  vertical-align: text-bottom;
  border: 0.25em solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spinner-border .75s linear infinite;
}
.scrollbar-thin { scrollbar-width: thin; }
.scrollbar-thumb-gray-400::-webkit-scrollbar-thumb { background-color: var(--fourth); border-radius: 4px; }
.scrollbar-track-gray-200::-webkit-scrollbar-track { background-color: var(--primary); }
::-webkit-scrollbar { width: 8px; }
.search-highlight {
    background-color: #ffe066;
    color: #222;
    padding: 0 2px;
    border-radius: 2px;
}

/* Visual indicator for active audio call on chat button */
.chat-modal.audio-call-active-indicator {
    animation: call-indicator-pulse 2s infinite;
    box-shadow: 0 0 0 0 rgba(22, 163, 74, 0.7); /* Tailwind green-600 */
}
.chat-modal.audio-call-active-indicator .anticon-phone {
    color: #16A34A; /* Tailwind green-600 */
}

@keyframes call-indicator-pulse {
  0% { box-shadow: 0 0 0 0 rgba(22, 163, 74, 0.7); } /* Tailwind green-600 */
  70% { box-shadow: 0 0 0 10px rgba(22, 163, 74, 0); } /* Tailwind green-600 */
  100% { box-shadow: 0 0 0 0 rgba(22, 163, 74, 0); } /* Tailwind green-600 */
}

/* Pink button for gender selection */
.btn-outline-pink {
  --bs-btn-color: #E91E63;
  --bs-btn-border-color: #dee2e6; /* Neutral border for default outline state */
  --bs-btn-hover-color: #fff;
  --bs-btn-hover-bg: #E91E63;
  --bs-btn-hover-border-color: #E91E63;
  --bs-btn-focus-shadow-rgb: 233, 30, 99;
  --bs-btn-active-color: #fff;
  --bs-btn-active-bg: #E91E63;
  --bs-btn-active-border-color: #E91E63;
  --bs-btn-disabled-color: #E91E63;
  --bs-btn-disabled-bg: transparent;
  --bs-btn-disabled-border-color: #E91E63;
}

.btn-check:checked + .btn-outline-pink,
.btn-check:active + .btn-outline-pink,
.btn-outline-pink:active,
.btn-outline-pink.active,
.btn-outline-pink.dropdown-toggle.show {
  color: #fff;
  background-color: #E91E63;
  border-color: #E91E63;
}

.btn-outline-pink:disabled,
.btn-outline-pink.disabled {
  color: #E91E63;
  background-color: transparent;
  border-color: #E91E63;
}

/* Theme-color button (e.g., Masculina) default and active/checked state */
/* Default state for .btn-outline-theme-color */
.btn-outline-theme-color {
  --bs-btn-color: var(--theme-color);
  --bs-btn-border-color: #dee2e6; /* Neutral border for default outline state */
  /* Retain existing hover/active definitions if they are suitable or define them here too */
  --bs-btn-hover-color: #fff;
  --bs-btn-hover-bg: var(--theme-color);
  --bs-btn-hover-border-color: var(--theme-color);
  --bs-btn-active-color: #fff;
  --bs-btn-active-bg: var(--theme-color);
  --bs-btn-active-border-color: var(--theme-color);
  --bs-btn-disabled-color: var(--theme-color);
  --bs-btn-disabled-bg: transparent;
  --bs-btn-disabled-border-color: #dee2e6;
}

.btn-check:checked + .btn-outline-theme-color,
.btn-check:active + .btn-outline-theme-color, /* Might be overly broad, :checked is key */
.btn-outline-theme-color:active,
.btn-outline-theme-color.active,
.btn-outline-theme-color.dropdown-toggle.show {
  color: #fff; /* White text for contrast */
  background-color: var(--theme-color); /* Fill with theme color */
  border-color: var(--theme-color); /* Border to match background */
}

/* Hover specifically for filling, if not fully covered by --bs-btn-hover-* vars */
.btn-outline-theme-color:hover {
    color: #fff;
    background-color: var(--theme-color);
    border-color: var(--theme-color);
}

/* Styles for when .btn-outline-theme-color is disabled */
.btn-outline-theme-color:disabled,
.btn-outline-theme-color.disabled {
  color: var(--theme-color); /* Text remains theme color */
  background-color: transparent; /* No background fill */
  border-color: #dee2e6; /* Neutral border when disabled */
  /* Opacity is often handled by Bootstrap's base .disabled styles */
}

/* Styles for the top-screen alert */
#top-screen-alert-container {
    position: fixed;
    top: 10px; /* Adjust as needed */
    left: 50%;
    transform: translateX(-50%);
    z-index: 9999999; /* High z-index to appear on top */
    width: auto; /* Auto width based on content */
    max-width: 90%; /* Max width to prevent it from being too wide on larger screens */
    box-sizing: border-box; /* Include padding and border in the element's total width and height */
}

.bootstrap-alert {
    box-shadow: 0 4px 8px rgba(0,0,0,0.15);
    padding: 0.75rem 1.25rem;
    /* Standard Bootstrap alert padding */
    margin-bottom: 1rem;
    border: 1px solid transparent;
    border-radius: .375rem; /* Slightly larger radius for modern look */
    display: flex; /* For icon and text alignment */
    align-items: center; /* For icon and text alignment */
    word-wrap: break-word; /* Allow long words to break and wrap */
    overflow-wrap: break-word; /* Ensure wrapping for long words or strings */
    max-height: 80vh; /* Limit maximum height to 80% of viewport height */
    overflow-y: auto; /* Enable vertical scrolling if content exceeds max-height */
}

.bootstrap-alert .btn-close {
    padding: 0.5rem 0.5rem; /* Adjust padding for dismiss button */
    margin-left: 1rem;
}

/* Specific alert types - using Bootstrap variable names if available, else defining colors */
.alert-danger {
    color: #721c24; /* Darker text for better contrast */
    background-color: #f8d7da;
    border-color: #f5c6cb;
}

.alert-info {
    color: #0c5460; /* Darker text */
    background-color: #d1ecf1;
    border-color: #bee5eb;
}

.alert-info .anticon-phone {
    color: #0c5460; /* Match text color */
}

.darkmode #top-screen-alert-container .alert-danger {
    color: #f8d7da; /* Lighter text for dark mode */
    background-color: #721c24;
    border-color: #f5c6cb; /* Keep border light for contrast */
}

.darkmode #top-screen-alert-container .alert-info {
    color: #d1ecf1; /* Lighter text for dark mode */
    background-color: #0c5460;
    border-color: #bee5eb; /* Keep border light for contrast */
}

.darkmode #top-screen-alert-container .alert-info .anticon-phone {
    color: #d1ecf1; /* Match text color for dark mode */
}

@media (max-width: 576px) {
    #top-screen-alert-container {
        max-width: 95%; /* Increase max-width for very small screens */
        top: 5px; /* Adjust top position for smaller screens */
    }
    .bootstrap-alert {
        padding: 0.5rem 1rem; /* Reduce padding on smaller screens */
        font-size: 0.875rem; /* Adjust font size for smaller screens */
    }
    .bootstrap-alert .btn-close {
        padding: 0.4rem 0.4rem; /* Adjust close button padding */
    }
}

/* Search History Navigation Styles */
#search-history-nav {
    padding: 0.75rem;
    background-color: var(--fourth);
    border-radius: 0.5rem;
    margin: 1rem 0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
}

#search-history-nav button {
    min-width: auto;
    padding: 0.375rem 0.75rem;
    font-size: 0.875rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    transition: all 0.2s;
    flex-shrink: 0;
}

#search-history-nav button:hover:not(:disabled) {
    background-color: var(--theme-color);
    border-color: var(--theme-color);
    color: white;
}

#search-history-nav button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

#search-history-label {
    flex: 1 1 auto;
    text-align: center;
    font-size: 0.875rem;
    color: var(--secondary);
    font-weight: 500;
    line-height: 1.4;
    word-wrap: break-word;
    overflow-wrap: break-word;
    min-width: 0; /* Allow flex item to shrink below content size */
    padding: 0.25rem 0.5rem;
}

.search-query-text {
    display: inline-block;
    max-width: 100%;
    vertical-align: middle;
}

.search-counter {
    display: inline-block;
    font-size: 0.75rem;
    color: var(--third);
    margin-left: 0.5rem;
}

/* Responsive styles for search history navigation */
@media (max-width: 768px) {
    #search-history-nav {
        padding: 0.625rem;
        flex-direction: column;
        text-align: center;
    }

    #search-history-label {
        order: -1;
        width: 100%;
        margin-bottom: 0.5rem;
        font-size: 0.8125rem;
        border-bottom: 1px solid var(--border-color, #dee2e6);
        padding-bottom: 0.5rem;
    }

    .search-query-text {
        display: block;
        margin-bottom: 0.25rem;
        font-weight: 600;
    }

    .search-counter {
        display: block;
        margin-left: 0;
    }

    #search-history-nav .btn-container {
        display: flex;
        gap: 0.5rem;
        justify-content: center;
        width: 100%;
    }

    #search-history-nav button {
        flex: 1;
        max-width: 150px;
    }
}

@media (max-width: 480px) {
    #search-history-nav {
        padding: 0.5rem;
        margin: 0.75rem 0;
    }

    #search-history-nav button {
        padding: 0.375rem 0.625rem;
        font-size: 0.8125rem;
    }

    #search-history-label {
        font-size: 0.75rem;
        padding-bottom: 0.375rem;
    }

    .search-counter {
        font-size: 0.6875rem;
    }
}

/* Tooltip styles for full query on hover/touch */
#search-history-label[title] {
    cursor: help;
    position: relative;
}

/* Dark mode adjustments */
.darkmode #search-history-nav {
    background-color: var(--fourth);
    box-shadow: 0 2px 4px rgba(255, 255, 255, 0.1);
}

.darkmode #search-history-label {
    color: var(--secondary);
}

.darkmode .search-counter {
    color: var(--third);
}

/* Styles for Document History Navigation (similar to search) */
#document-history-nav {
    padding: 0.75rem;
    background-color: var(--fourth); /* Assuming var(--fourth) is defined */
    border-radius: 0.5rem;
    margin: 1rem 0;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
}

#document-history-nav button {
    min-width: auto;
    padding: 0.375rem 0.75rem;
    font-size: 0.875rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    white-space: nowrap;
    transition: all 0.2s;
    flex-shrink: 0;
    /* Assuming btn and btn-outline-secondary styles are globally available or define them */
}

#document-history-nav button:hover:not(:disabled) {
    background-color: var(--theme-color); /* Assuming var(--theme-color) is defined */
    border-color: var(--theme-color);
    color: white;
}

#document-history-nav button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

#document-history-label {
    flex: 1 1 auto;
    text-align: center;
    font-size: 0.875rem;
    color: var(--secondary); /* Assuming var(--secondary) is defined */
    font-weight: 500;
    line-height: 1.4;
    word-wrap: break-word;
    overflow-wrap: break-word;
    min-width: 0;
    padding: 0.25rem 0.5rem;
}

.document-title-text { /* For the document identifier part of the label */
    display: inline-block;
    max-width: 100%;
    vertical-align: middle;
}

.document-counter { /* For the (1/N) part of the label */
    display: inline-block;
    font-size: 0.75rem;
    color: var(--third); /* Assuming var(--third) is defined */
    margin-left: 0.5rem;
}

/* Responsive styles for document history navigation */
@media (max-width: 768px) {
    #document-history-nav {
        padding: 0.625rem;
        flex-direction: column;
        text-align: center;
    }

    #document-history-label {
        order: -1;
        width: 100%;
        margin-bottom: 0.5rem;
        font-size: 0.8125rem;
        border-bottom: 1px solid var(--border-color, #dee2e6); /* Assuming var(--border-color) or fallback */
        padding-bottom: 0.5rem;
    }

    .document-title-text {
        display: block;
        margin-bottom: 0.25rem;
        font-weight: 600;
    }

    .document-counter {
        display: block;
        margin-left: 0;
    }

    #document-history-nav .btn-container { /* If you wrap buttons in a div */
        display: flex;
        gap: 0.5rem;
        justify-content: center;
        width: 100%;
    }

    #document-history-nav button {
        flex: 1;
        max-width: 150px; /* Or as appropriate */
    }
}

@media (max-width: 480px) {
    #document-history-nav {
        padding: 0.5rem;
        margin: 0.75rem 0;
    }

    #document-history-nav button {
        padding: 0.375rem 0.625rem;
        font-size: 0.8125rem;
    }

    #document-history-label {
        font-size: 0.75rem;
        padding-bottom: 0.375rem;
    }

    .document-counter {
        font-size: 0.6875rem;
    }
}

/* Dark mode adjustments for document history */
.darkmode #document-history-nav {
    background-color: var(--fourth); /* Adjust if needed for dark mode */
    box-shadow: 0 2px 4px rgba(255, 255, 255, 0.1); /* Lighter shadow for dark mode */
}

.darkmode #document-history-label {
    color: var(--secondary); /* Adjust if needed */
}

.darkmode .document-counter {
    color: var(--third); /* Adjust if needed */
}

/* Audio Call Context Documents List Border */
#audioCallContextDocumentsList {
    border: 1px solid var(--fourth);
    border-radius: 0.375rem;
    padding: 0.5rem;
    background-color: var(--primary);
}

.darkmode #audioCallContextDocumentsList {
    border-color: var(--fourth);
    background-color: var(--primary);
}

/* System Message Styles */
.system-message {
    background-color: #f8fafc !important;
    border-left: 4px solid #3b82f6 !important;
    margin-bottom: 1rem;
}

.system-message .system-message-content {
    font-size: 0.875rem;
    line-height: 1.5;
}

.system-message .system-message-content table {
    font-size: 0.8125rem;
    margin-top: 0.5rem;
    margin-bottom: 0.5rem;
}

.system-message .system-message-content table th {
    background-color: #e2e8f0;
    font-weight: 600;
    padding: 0.5rem;
    border: 1px solid #cbd5e1;
}

.system-message .system-message-content table td {
    padding: 0.375rem 0.5rem;
    border: 1px solid #cbd5e1;
    vertical-align: top;
}

.system-message .system-message-content table td a {
    color: #3b82f6;
    text-decoration: underline;
    word-break: break-all;
}

.system-message .system-message-content table td a:hover {
    color: #1d4ed8;
}

.system-message .system-message-content strong {
    color: #1e40af;
    font-weight: 600;
}

/* Dark mode adjustments for system messages */
.darkmode .system-message {
    background-color: #1e293b !important;
    border-left-color: #60a5fa !important;
}

.darkmode .system-message .system-message-content {
    color: #e2e8f0;
}

.darkmode .system-message .system-message-content table th {
    background-color: #374151;
    color: #f9fafb;
    border-color: #4b5563;
}

.darkmode .system-message .system-message-content table td {
    border-color: #4b5563;
    color: #e5e7eb;
}

.darkmode .system-message .system-message-content table td a {
    color: #60a5fa;
}

.darkmode .system-message .system-message-content table td a:hover {
    color: #93c5fd;
}

.darkmode .system-message .system-message-content strong {
    color: #93c5fd;
}

/* Error type system messages */
.system-message.error-message {
    background-color: #fef2f2 !important;
    border-left-color: #ef4444 !important;
}

.system-message.error-message .text-blue-800 {
    color: #dc2626 !important;
}

.system-message.error-message .bg-blue-100 {
    background-color: #fecaca !important;
}

.system-message.error-message .text-blue-600 {
    color: #dc2626 !important;
}

.darkmode .system-message.error-message {
    background-color: #450a0a !important;
    border-left-color: #f87171 !important;
}

.darkmode .system-message.error-message .text-blue-800 {
    color: #fca5a5 !important;
}

.darkmode .system-message.error-message .bg-blue-100 {
    background-color: #7f1d1d !important;
}

.darkmode .system-message.error-message .text-blue-600 {
    color: #fca5a5 !important;
}
`;
document.head.appendChild(style);

export default { AudioCallHandler }; 
